/* eslint-disable @typescript-eslint/no-explicit-any */
import { createMemo, createRoot } from "solid-js";
import { createStore, reconcile } from "solid-js/store";

import {
  postDoManualExchangeBonus,
  postGetMyBonusTransaction,
  postGetSpecialActivityRewards,
} from "@/api/clientApi/activity";
import { DialogType } from "@/components/Dialogs/types";
import { useDialog } from "@/components/Dialogs/useDialog";
import { Option } from "@/components/PC/Register/type";
import {
  NormalActList,
  VipActList,
  newComerActList,
  wagerContestActList,
} from "@/constants/constants";
import { RepeatActivity, RepeatCompetitionRankType } from "@/enum/activity";
import { useFreeRelief } from "@/hooks/activity/useFreeRelief";
import { propsStore } from "@/hooks/useProps";
import { useRepeatCompetition } from "@/hooks/useRepeatCompetition";
import { useVipActivity } from "@/hooks/useVipActivity";
import { wagerTabList } from "@/hooks/useWagerContest";
import { getContentByLang, getLangObject, useI18n } from "@/i18n";
import { formatDate } from "@/lib/dateUtils";
import { isMobile } from "@/lib/isMobile";
import {
  formatToBindPoint,
  renderFiatOrPlatCoinSymbol,
} from "@/lib/pointUtils";
import { getImgurl } from "@/lib/resFolder";
import {
  checkTimeInPeriods,
  getTimeRemaining,
  isAfun,
  isInSpecialPeriod,
  renderInnerHtml,
} from "@/lib/utils";
import {
  ActivityDetailStatus,
  ActivityTypeEnum,
  BonusBgMode,
} from "@/types/compontent/base/baseEnum";
import {
  ActivityCfg,
  ActivityDetailsRewards,
  DetailsStatus,
} from "@/types/swagger/activity";
import {
  MyBonusTransactionRec,
  MyBonusTransactionReq,
} from "@/types/swagger/front";
import {
  GetMyTaskActivityCfgInfo,
  NewActivityDetails,
  RewardRecvCond,
  SpecialActivityRewards,
} from "@/types/swaggerex/activity";

import {
  activityStore,
  getActivityCfg,
  getActivityDetails,
  setActivityStore,
} from "./activityStore";
import { getCrypto } from "./cryptoStore";
import { sysConfigStore } from "./sysConfigStore";
import { bindPointInfo, getUserInfo, userStore } from "./userStore";

const { t } = useI18n();
// 常量定义
const DEFAULT_PAGE_SIZE = 8;
const INITIAL_PAGE = 1;
const DEFAULT_BONUS_CATEGORY = 0;
const MAX_CHECKIN_DAYS = 7;

// 类型定义
export type BonusCardData = {
  atype: number;
  title: string;
  request: boolean;
  details: any;
  rewards: ActivityDetailsRewards | any;
  details_status: DetailsStatus;
  bg: string;
  order: number;
  hide: boolean;
  vip: boolean;
  active_img: string;
  img: string;
  other: any;
  content: string;
  status: ActivityDetailStatus;
  button: string;
  bg_mode: number;
  text: Record<string, string | boolean>[] | string;
  hasRules: boolean;
  readyList: ReadyItem[];
};

export type ReadyItem = {
  type: number;
  atype: number;
  img: string;
  order: number;
  title: string;
  button: string;
  status: ActivityDetailStatus;
  receive_point: number;
  id: number;
};

export type SumData = {
  totalActivity: number;
  totalVip: number;
  totalAll: number;
  totalSpecial: number;
  specialHistoryTypeList: number[];
  specialHistoryMaps: Record<number, number>;
};

export type BonusCardDataMap = Record<number | string, BonusCardData>;

export type PageData = {
  tabIndex: number;
  ruleAtype: number;
  category: string;
};

export type MyBonusTransactionItem = {
  bill_type: number;
  a_type: number;
  recv_at: number;
  point_type: number;
  point: number;
  crypto_point: number;
  bonus_category: number;
  web_render_time: string;
};

// Store 初始化
const initialStore = {
  redeemRuleSwitch: false,
  redeemWagerSwitch: false,
  bonusCenterCardData: {} as BonusCardDataMap,
  bonusSumaryData: {} as SumData,
  pageData: {
    tabIndex: 0,
    ruleAtype: 0,
    category: "",
  } as PageData,
  tabList: [
    {
      title: "bonus.newcomers",
      index: 0,
      count: 0,
      type: 0,
    },
    {
      title: "bonus.general",
      index: 1,
      count: 0,
      type: 0,
    },
    { title: "VIP", index: 2, count: 0, type: 0 },
    {
      title: "bonus.special",
      index: 3,
      count: 0,
      type: 0,
    },
  ],
  specialList: [] as any[],
  repeatCompetitionRewards: [] as any[],
  bonusTransaction: {
    finish: false,
    list: [] as MyBonusTransactionItem[],
    data: {
      page: INITIAL_PAGE,
      page_size: DEFAULT_PAGE_SIZE,
      bonus_category: DEFAULT_BONUS_CATEGORY,
    },
  },

  taskList: [] as GetMyTaskActivityCfgInfo[],
  redeem_code: "",
  //优惠活动数据
  specialDetail: {
    show: false,
    data: {
      reward_recv_cond: {} as RewardRecvCond,
      name: {},
      img: {},
    } as SpecialActivityRewards,
  },
};
const { repeatCompetitionStore, initData, getUserActivityDetails } =
  useRepeatCompetition();
export const [bonusCenterStore, setBonusCenterStore] =
  createStore(initialStore);
// 工具函数
const serverDayEndTime = () => {
  const serverDate = new Date(parseInt(`${Date.now() / 1000}`) * 1000);
  const year = serverDate.getFullYear();
  const month = serverDate.getMonth();
  const day = serverDate.getDate();
  return new Date(year, month, day, 23, 59, 59).getTime() / 1000;
};

export const getSpecialActivityRewards = async () => {
  const res = await postGetSpecialActivityRewards(); // Renamed to avoid recursion
  const noCondRewards = Array.isArray(res?.no_cond_rewards)
    ? res.no_cond_rewards
    : [];
  const condRewards = Array.isArray(res?.cond_rewards) ? res.cond_rewards : [];
  const list = [...noCondRewards, ...condRewards];
  const specials: any[] = [];
  const repeatCompetitionRewards: any[] = [];
  list.forEach((el) => {
    if (el.activity_type == RepeatActivity.RepeatCompetition) {
      repeatCompetitionRewards.push(el);
    } else {
      specials.push(el);
    }
  });
  specials.sort((a: any, b: any) => {
    if (a.is_receivable === b.is_receivable) {
      return Number(a.sort) - Number(b.sort);
    } else {
      return Number(b.is_receivable) - Number(a.is_receivable);
    }
  });
  setBonusCenterStore(
    "repeatCompetitionRewards",
    reconcile(repeatCompetitionRewards),
  );
  setBonusCenterStore("specialList", reconcile(specials));
  updateTabList();
};

// 计算特殊活动所有条件
export const specialCalc = () => {
  const from = bonusCenterStore.specialDetail.data.reward_recv_cond || {};
  let count = 0;
  let all = 0;
  const maps: any = {};
  if (from.vip_lv_limit) {
    all++;
    const complete = from.fin_vip_lv_limit_prograss >= from.vip_lv_limit;
    if (complete) {
      count++;
    }
    maps.vip_lv_limit = {
      count: from.fin_vip_lv_limit_prograss,
      limit: from.vip_lv_limit,
      progress:
        ((from.fin_vip_lv_limit_prograss || 0) / from.vip_lv_limit) * 100,
      numberType: 1,
      complete,
    };
  }
  if (from.dis_reward_need_app?.length) {
    all++;
    const complete = from.hit_dis_reward_client;
    if (complete) {
      count++;
    }
    maps.dis_reward_need_app = {
      count: from.hit_dis_reward_client ? 1 : 0,
      limit: 1,
      progress: from.hit_dis_reward_client ? 100 : 0,
      numberType: 1,
      complete,
    };
  }
  if (from.bind_email) {
    all++;
    const complete = from.hit_bind_email;
    if (complete) {
      count++;
    }
    maps.bind_email = {
      count: from.hit_bind_email ? 1 : 0,
      limit: 1,
      progress: from.hit_bind_email ? 100 : 0,
      numberType: 1,
      complete,
    };
  }
  if (from.bind_phone) {
    all++;
    const complete = from.hit_bind_phone;
    if (complete) {
      count++;
    }
    maps.bind_phone = {
      count: from.hit_bind_phone ? 1 : 0,
      limit: 1,
      progress: from.hit_bind_phone ? 100 : 0,
      numberType: 1,
      complete,
    };
  }
  if (from.bind_c_p_f) {
    all++;
    const complete = from.hit_bind_c_p_f;
    if (complete) {
      count++;
    }
    maps.bind_c_p_f = {
      count: from.hit_bind_c_p_f ? 1 : 0,
      limit: 1,
      progress: from.hit_bind_c_p_f ? 100 : 0,
      numberType: 1,
      complete,
    };
  }
  if (from.bind_withdraw_card) {
    all++;
    const complete = from.hit_bind_withdraw_card;
    if (complete) {
      count++;
    }
    maps.bind_withdraw_card = {
      count: from.hit_bind_withdraw_card ? 1 : 0,
      limit: 1,
      progress: from.hit_bind_withdraw_card ? 100 : 0,
      numberType: 1,
      complete,
    };
  }
  if (from.game_amount_limit_mode > 0 && from.game_amount_limit > 0) {
    all++;
    const complete =
      from.fin_game_amount_limit_prograss >= from.game_amount_limit;
    if (complete) {
      count++;
    }
    maps.game_amount_limit = {
      count: from.fin_game_amount_limit_prograss,
      limit: from.game_amount_limit,
      progress:
        ((from.fin_game_amount_limit_prograss || 0) / from.game_amount_limit) *
        100,
      numberType: 2,
      mode: from.games_limit_mode,
      list: from.games_limit,
      complete,
    };
  }
  if (from.calc_deposit_amount_mode > 0 && from.deposit_amount_limit > 0) {
    // 存款金额限制
    all++;
    const complete =
      from.fin_deposit_amount_limit_prograss >= from.deposit_amount_limit;
    if (complete) {
      count++;
    }
    maps.deposit_amount_limit = {
      count: from.fin_deposit_amount_limit_prograss,
      limit: from.deposit_amount_limit,
      progress:
        ((from.fin_deposit_amount_limit_prograss || 0) /
          from.deposit_amount_limit) *
        100,
      numberType: 2,
      complete,
    };
  }

  if (from.calc_deposit_cnt_mode > 0 && from.deposit_cnt_limit > 0) {
    // 存款次数限制
    all++;
    const complete =
      from.fin_deposit_cnt_limit_prograss >= from.deposit_cnt_limit;
    if (complete) {
      count++;
    }
    maps.deposit_cnt_limit = {
      count: from.fin_deposit_cnt_limit_prograss,
      limit: from.deposit_cnt_limit,
      progress:
        ((from.fin_deposit_cnt_limit_prograss || 0) / from.deposit_cnt_limit) *
        100,
      numberType: 1,
      complete,
    };
  }
  if (from.calc_child_user_num_mode > 0 && from.child_user_num_limit > 0) {
    // 下次数量限制
    all++;
    const complete =
      from.fin_child_user_num_limit_prograss >= from.child_user_num_limit;
    if (complete) {
      count++;
    }
    maps.child_user_num_limit = {
      count: from.fin_child_user_num_limit_prograss,
      limit: from.child_user_num_limit,
      progress:
        ((from.fin_child_user_num_limit_prograss || 0) /
          from.child_user_num_limit) *
        100,
      numberType: 1,
      complete,
    };
  }
  return {
    end_itime: bonusCenterStore.specialDetail.data.end_itime,
    all,
    count,
    progress: all ? count / all : 0,
    is_receivable: bonusCenterStore.specialDetail.data.is_receivable,
    ...maps,
  };
};

export const togglesSpecialDetail = (item: SpecialActivityRewards) => {
  const { showDialog } = useDialog();

  const data: SpecialActivityRewards = {
    ...item,
    reward_recv_cond: item.reward_recv_cond || {},
    name: item.name || {},
    desc: item.desc || {},
    img: item.img || {},
  };
  setBonusCenterStore("specialDetail", {
    show: !!item.id,
    data: data,
  });

  if (item.id) {
    showDialog({
      type: DialogType.SpecialBonus,
    });
  }
};

const processSuperValueGiftBoxCardData = (
  cardData: BonusCardData,
  configData: ActivityCfg,
): BonusCardData => {
  const { t } = useI18n();

  const updatedCardData = {
    ...cardData,
    title: t("luckypack.title"),
    bg_mode: BonusBgMode.yellow,
    vip: false,
    bg: isMobile()
      ? getImgurl("h5_bj_4.jpg", "bonusCenter/cardBg")
      : getImgurl("pc_bj_4.jpg", "bonusCenter/cardBg"),
  };

  if (updatedCardData.hide) return updatedCardData;

  let button = t("bonus.details");
  if (updatedCardData.details.expire_at) {
    button = t("common.purchase");
  }

  const text: any[] = [
    {
      title: t("bonus.price"),
      value:
        formatToBindPoint({
          value: (configData.cfg as any).price_point,
          isConverted: false,
          isReward: true,
        }) + "",
    },
    {
      title: t("bonus.reward_value"),
      value:
        formatToBindPoint({
          value: (configData.cfg as any).worth_point,
          isConverted: false,
          isReward: true,
        }) + "",
    },
  ];
  const remainTime = getTimeRemaining(updatedCardData.details.expire_at);
  const value3 = remainTime.total > 0 ? "00:00:00" : "-";

  if (remainTime.total > 0) {
    text.push({
      title: t("bonus.time_left"),
      value: value3,
      countDown: true,
      endTime: updatedCardData.details.expire_at,
    });
  }

  let readyList: ReadyItem[] = [];
  const readyItem = getReadyItem(updatedCardData, {
    button: t("common.purchase"),
  });
  readyList = readyItem ? [readyItem] : [];

  return {
    ...updatedCardData,
    button,
    text,
    readyList,
  };
};

// 活动处理函数
const processVipUpgradeCardData = (
  cardData: BonusCardData,
  configData: ActivityCfg,
): BonusCardData => {
  const updatedCardData = {
    ...cardData,
    title: t("bonus.vip_upgrade"),
    bg_mode: BonusBgMode.yellow,
    vip: true,
    bg: isMobile()
      ? getImgurl("h5_bj_2.jpg", "bonusCenter/cardBg")
      : getImgurl("pc_bj_2.jpg", "bonusCenter/cardBg"),
  };

  if (updatedCardData.hide) return updatedCardData;

  let button = t("bonus.view_vip_club");
  const status = updatedCardData.status;
  if (status == ActivityDetailStatus.canComplete) {
    button = t("common.claim");
  } else if (status == ActivityDetailStatus.canReceive) {
    button = t("common.claim");
  } else if (status == ActivityDetailStatus.toReceive) {
    button = t("common.claim");
  }

  const next =
    (configData.cfg as any).levels?.find(
      (el: any) => el.vip > (userStore.userInfo?.UserVip?.vip_level || 0),
    ) || {};

  const readyList: ReadyItem[] = [];

  updatedCardData.rewards?.list?.forEach((el: any) => {
    const readyItem = getReadyItem(updatedCardData, {
      button: t("bonusCenter.receive"),
      type: 2,
      receive_point: el.point,
      id: el.id,
    });
    if (readyItem) {
      readyList.push(readyItem);
    }
  });

  const levels = createMemo(
    () => sysConfigStore.sysConfigData?.vip_config?.levels,
  );
  const nextVip = Math.min(
    (userStore.userInfo?.UserVip?.vip_level || 0) + 1,
    levels()?.[levels()?.length - 1]?.vip,
  );

  const text = [];

  const currCoin = (userStore.userInfo?.UserVip?.vip_coin || 0) / 100;
  const nextCoin = (userStore.userInfo?.UserVip?.next_coin || 0) / 100;
  const value1 = userStore.userInfo?.UserVip?.next_coin
    ? `${renderInnerHtml(
        currCoin.toFixed(2) + "",
        "text-general-light",
      )} / ${renderInnerHtml(
        (currCoin + nextCoin).toFixed(2),
        "text-general-bright",
      )}`
    : "-";

  text.push({
    title: t("bonus.xp_to_vip", { val: nextVip }),
    value: value1,
  });

  const value2 =
    formatToBindPoint({
      value: (next as any)?.reward?.point || 0,
      isConverted: false,
      isReward: true,
    }) + "";
  text.push({
    title: t("bonus.next_reward"),
    value: value2,
  });

  return {
    ...updatedCardData,
    status,
    other: next,
    readyList,
    button,
    text,
  };
};

const processDailyLossRebateCardData = (
  cardData: BonusCardData,
  configData: ActivityCfg,
): BonusCardData => {
  const updatedCardData = {
    ...cardData,
    title: t("reward.daily_lossback"),
    bg_mode: BonusBgMode.yellow,
    vip: false,
    bg: isMobile()
      ? getImgurl("h5_bj_1.jpg", "bonusCenter/cardBg")
      : getImgurl("pc_bj_1.jpg", "bonusCenter/cardBg"),
  };

  if (updatedCardData.hide) return updatedCardData;

  const endTime =
    serverDayEndTime() + ((configData.cfg as any).settle_hour || 0) * 60 * 60;

  const button = t("common.claim");

  const text: any[] = [];
  const currentLevel = cardData.details?.curr_level_cfg || null;
  const nextLevel = cardData.details?.next_level_cfg || null;
  let title = "";
  let value = "";
  if (currentLevel && currentLevel.reward?.reward_point_mode == 1) {
    //固定比例
    value = `${renderInnerHtml(
      formatToBindPoint({
        value: cardData.details?.loss_point || 0,
        isConverted: false,
        isReward: true,
      }) + "",
      "text-general-light",
    )} / ${renderInnerHtml(
      formatToBindPoint({
        value: nextLevel?.loss_point || 0,
        isConverted: false,
        isReward: true,
      }) + "",
      "text-general-bright",
    )}`;
    title = t("bonus.loss_required");
  } else if (currentLevel && currentLevel.reward?.reward_point_mode == 5) {
    //固定金额
    title = t("bonus.Rete");
    value = `${(currentLevel.reward?.flow_pro / 100).toFixed(2)}%`;
  }

  let other: any = {};

  const flow_map: any = parseLevelConfigToDirectMap(
    configData.cfg?.level_cfg || [],
  );

  other = {
    rebate_data: {
      directShow: true,
      directMap: flow_map,
    },
  };
  text.push({
    title,
    value,
    info: true,
  });
  text.push({
    title: t("bonus.expected_reward"),
    value: cardData.details?.next_day_point
      ? formatToBindPoint({
          value: cardData.details?.next_day_point || 0,
          isConverted: false,
          isReward: true,
        }) + ""
      : "-",
  });
  const readyList: ReadyItem[] = [];
  if (updatedCardData?.rewards?.list?.length) {
    updatedCardData.rewards.list.forEach((el: any) => {
      const readyItem = getReadyItem(updatedCardData, {
        button: t("common.claim"),
        type: 2,
        receive_point: el.point,
        id: el.id,
      });
      if (readyItem) {
        readyList.push(readyItem);
      }
    });
  } else if (updatedCardData.status == ActivityDetailStatus.toReceive) {
    const readyItem = getReadyItem(updatedCardData, {
      button: t("common.claim"),
      type: 2,
      receive_point: cardData.details?.next_day_point || 0,
      id: "",
    });
    if (readyItem) {
      readyList.push(readyItem);
    }
  }
  return {
    ...updatedCardData,
    details: {
      ...updatedCardData.details,
      end_time: endTime,
    },
    other,
    text,
    button,
    readyList,
  };
};

/** 解析 level_cfg 数据并组装成 directMap 格式 */

type LevelConfig = {
  loss_point: number;
  upper_limit_point: number;
  min_limit_point: number;
  reward: {
    pay_pro: number;
    rand_point_min: number;
    rand_pay_pro_max: number;
    rand_flow_pro_min: number;
    rand_flow_pro_max: number;
    name: string | null;
    props: unknown;
    code_rate: number;
    calc_code_formula: number;
    vip_int: number;
    point_type: number;
    code_game_classes: number[];
    reward_point_mode: number;
    rand_point_max: number;
    rand_pay_pro_min: number;
    point: number;
    flow_pro: number;
    wallet_type: number;
    reward_type: number;
    img: string | null;
    desc: string | null;
  };
};

type DirectMapItem = {
  flow: boolean;
  value: number | string;
};

type DirectMap = Record<string, DirectMapItem>;

/**
 * 解析 level_cfg 数据并组装成 directMap 格式
 *
 * @param levelConfigs Level_cfg 数组
 * @returns 符合 directMap 格式的对象
 */
export function parseLevelConfigToDirectMap(
  levelConfigs: LevelConfig[],
): DirectMap {
  const directMap: DirectMap = {};

  levelConfigs.forEach((config) => {
    const { loss_point, reward } = config;

    // 根据 reward_point_mode 决定 flow 和 value
    let flow: boolean;
    let value: number;

    if (reward.reward_point_mode === 1) {
      // reward_point_mode == 1: flow为false, value取reward.point
      flow = false;
      value = formatToBindPoint({
        value: reward.point,
        isConverted: false,
        isReward: true,
      });
    } else if (reward.reward_point_mode === 5) {
      // reward_point_mode == 5: flow为true, value取reward.flow_pro
      flow = true;
      value = (reward.flow_pro / 100).toFixed(2);
    } else {
      // 其他情况，默认使用 point 和 false
      flow = false;
      value = formatToBindPoint({
        value: reward.point,
        isConverted: false,
        isReward: true,
      });
    }

    // key 值为 >= loss_point
    const key = `>= ${formatToBindPoint({
      value: loss_point,
      isConverted: false,
      isReward: true,
    })}`;

    directMap[key] = {
      flow,
      value,
    };
  });

  return directMap;
}

/** 新人七日签到 */
const processNewCheckInCardData = (
  cardData: BonusCardData,
  configData: ActivityCfg,
): BonusCardData => {
  cardData.title = t("signin7.title");
  cardData.bg_mode = BonusBgMode.cyanBlue;
  cardData.vip = false;
  cardData.bg = isMobile()
    ? getImgurl("h5_bj_4.jpg", "bonusCenter/cardBg")
    : getImgurl("pc_bj_4.jpg", "bonusCenter/cardBg");

  if (cardData.hide) return cardData;

  const details = cardData.details || {};
  if (
    !cardData.rewards?.list?.length &&
    (details.nth > MAX_CHECKIN_DAYS || details.nth < 1 || details.nth == null)
  ) {
    cardData.hide = true;
    return cardData;
  }

  let button = t("bonus.details");
  const status = cardData.status;
  // if (cardData.rewards.list.length) {
  //   status = ActivityDetailStatus.canReceive;
  // }
  if (status == ActivityDetailStatus.canComplete) {
    button = t("common.claim");
  } else if (status == ActivityDetailStatus.canReceive) {
    button = t("common.claim");
  } else if (status == ActivityDetailStatus.toReceive) {
    button = t("common.claim");
  } else if (details.status_list && details.status_list[details.nth - 1] == 3) {
    button = "";
    details.end_time = serverDayEndTime();
  }

  const dataCfg = configData.cfg?.list?.[details.nth - 1] || {};
  const checked =
    details.status_list?.filter((val: any) => val == 3)?.length ?? 0;

  const other: any = {
    ...dataCfg,
    checked,
    deposit: details.deposit || 0,
    turnover: details.turnover || 0,
  };

  if (checked) {
    const str = `${details.start_date}`;
    const year = Number(str.slice(0, 4));
    const month = Number(str.slice(4, 6)) - 1;
    const day = Number(str.slice(6, 8));
    const end =
      Math.ceil(new Date(year, month, day + 6, 23, 59, 59).getTime() / 1000) +
      sysConfigStore.timeOffset;
    other.end = end;
  }

  const text = [
    {
      title: t("reward.count"),
      value: `${renderInnerHtml(checked + "", "text-general-light")} / ${renderInnerHtml(MAX_CHECKIN_DAYS + "", "text-general-bright")}`,
    },
  ];

  if (other.turnover_limit) {
    text.push({
      title: t("signin7.wager"),
      value: `${formatToBindPoint({
        value: other.turnover,
        isConverted: false,
        isReward: true,
      })} / ${formatToBindPoint({
        value: other.turnover_limit,
        isConverted: false,
        isReward: true,
        showSymbol: false,
      })}`,
    });
  }

  if (other.deposit_limit) {
    text.push({
      title: t("signin7.deposit"),

      value: `${formatToBindPoint({
        value: other.deposit,
        isConverted: false,
        isReward: true,
      })} / ${formatToBindPoint({
        value: other.deposit_limit,
        isConverted: false,
        isReward: true,
        showSymbol: false,
      })}`,
    });
  }

  const readyList = getReadyItem(cardData, {
    button: t("common.check_in"),
  });

  return {
    ...cardData,
    other,
    text,
    button,
    readyList: readyList ? [readyList] : [],
  };
};

/** 救济金3 */
const processReliefFund3CardData = (
  cardData: BonusCardData,
  configData: ActivityCfg,
): BonusCardData => {
  cardData.title = t("relieffund.title");
  cardData.bg_mode = BonusBgMode.green;
  cardData.vip = false;
  cardData.bg = isMobile()
    ? getImgurl("h5_bj_6.jpg", "bonusCenter/cardBg")
    : getImgurl("pc_bj_6.jpg", "bonusCenter/cardBg");

  // if (cardData.details.status == ActivityDetailStatus.canReceive) {
  //   cardData.hide = true;
  //   return cardData;
  // }
  const hide = cardData.status == ActivityDetailStatus.Finish;

  //只要后台配置显示就显示
  // if (
  //   cardData.details.received_point ||
  //   cardData.details.receivable_point === 0
  // ) {
  //   cardData.hide = true;
  //
  //  return cardData;
  // }

  let button = t("common.details");
  const status = cardData.status;
  // if (cardData.rewards.list.length) {
  //   status = ActivityDetailStatus.canReceive;
  // }
  if (status == ActivityDetailStatus.canComplete) {
    button = t("common.claim");
  } else if (status == ActivityDetailStatus.canReceive) {
    button = t("common.claim");
  } else if (status == ActivityDetailStatus.toReceive) {
    button = t("common.claim");
  }

  if (cardData.status === ActivityDetailStatus.canReceive) {
    button = t("common.claim");
    cardData.status =
      cardData.details_status?.new_status || ActivityDetailStatus.canReceive;
  }

  const readyList = getReadyItem(cardData, {
    button:
      cardData.status == ActivityDetailStatus.canReceive
        ? t("common.claim")
        : t("bonus.details"),
    type: 2,
    receive_point: cardData.details?.receivable_point || 0,
  });

  let text: string | Record<string, string>[] = [];
  const isPay = cardData.details?.first_pay_point > 0;
  const { max, maxPoint, min, minPoint, point1 } = useFreeRelief();
  const symbol = bindPointInfo()?.symbol;
  if (!isPay) {
    text = t("relieffund.description1", {
      val1: renderInnerHtml(`${symbol}${point1()}`, "text-general-light"),
      val2: renderInnerHtml(`${min() / 100}%`, "text-general-light"),
      val3: renderInnerHtml(`${max() / 100}%`, "text-general-light"),
    });
  }
  if (
    isPay &&
    cardData.status !== ActivityDetailStatus.canReceive &&
    cardData.status !== ActivityDetailStatus.toReceive
  ) {
    text = [];
    const price = formatToBindPoint({
      value: cardData.details?.first_pay_point,
      isConverted: false,
      isReward: true,
    });
    text.push({
      title: t("reward.first_deposit"),
      value: price,
    });
    // 最高奖励上线
    const maxPointLimit =
      configData?.cfg.reward_cfgs?.reduce((pre, item) => {
        if (item?.reward_point_up_limit > pre) {
          pre = item?.reward_point_up_limit;
        }
        return pre;
      }, Number(0)) || 0;
    text.push({
      title: t("bonus.expected_reward"),
      value: `${symbol}${Number(minPoint) > Number(maxPointLimit / 10000) ? `${maxPointLimit / 10000}` : minPoint()} - ${symbol}${Number(maxPoint()) > Number(maxPointLimit / 10000) ? `${maxPointLimit / 10000}` : maxPoint()}`,
    });
  }
  if (
    isPay &&
    (cardData.status == ActivityDetailStatus.canReceive ||
      cardData.status == ActivityDetailStatus.toReceive)
  ) {
    text = [
      {
        title: t("bonus.reward"),
        value: formatToBindPoint({
          value: cardData.details?.receivable_point,
          isConverted: false,
          isReward: true,
        }),
      },
    ];
  }

  return {
    ...cardData,
    button,
    readyList: readyList ? [readyList] : [],
    text,
    hide,
  };
};

/** 新人转盘 */
const processNewbieWheelCardData = (cardData: BonusCardData): BonusCardData => {
  cardData.title = t("spinnew.title");
  cardData.bg_mode = BonusBgMode.green;
  cardData.vip = false;
  cardData.bg = isMobile()
    ? getImgurl("h5_bj_3.jpg", "bonusCenter/cardBg")
    : getImgurl("pc_bj_3.jpg", "bonusCenter/cardBg");

  if (cardData.hide) return cardData;

  if (cardData.details?.status == ActivityDetailStatus.canReceive) {
    cardData.hide = true;
    return cardData;
  }

  // 额外处理是否隐藏结束
  let button = t("bonus.details");
  const list = bonusCenterStore.taskList?.filter(
    (el: GetMyTaskActivityCfgInfo) => el.task_week == 4,
  );

  const task_end_count = list?.filter((el: any) => el.task_status != 0).length;
  const task_count = list.length;
  if (
    cardData.status == ActivityDetailStatus.canComplete ||
    cardData.status == ActivityDetailStatus.canReceive ||
    cardData.status == ActivityDetailStatus.toReceive
  ) {
    button = t("common.claim");
  } else if (cardData.details?.had_tickets) {
    button = t("common.claim");
  }

  const text = [
    {
      title: t("spinnew.remainingtimes"),
      value: cardData.details?.had_tickets + "",
    },
    {
      title: t("spinnew.quest"),
      value: `${renderInnerHtml(task_end_count + "", "text-general-light")} / ${renderInnerHtml(task_count + "", "text-general-bright")}`,
    },
  ];

  let readyList: ReadyItem[] = [];
  const readyItem = getReadyItem(cardData, { button: button });
  readyList = readyItem ? [readyItem] : [];
  return {
    ...cardData,
    button,
    readyList: readyList,
    other: {
      task_end_count,
      task_count,
    },
    text,
  };
};

/** 扭蛋活动 */
const processGachaponCardData = (cardData: BonusCardData): BonusCardData => {
  cardData.title = t("gacha.title");
  cardData.bg_mode = BonusBgMode.green;
  cardData.vip = false;
  cardData.bg = isMobile()
    ? getImgurl("h5_bj_3.jpg", "bonusCenter/cardBg")
    : getImgurl("pc_bj_3.jpg", "bonusCenter/cardBg");

  if (cardData.hide) return cardData;

  let button = t("bonus.details");
  if (
    cardData.status == ActivityDetailStatus.canComplete ||
    cardData.status == ActivityDetailStatus.canReceive ||
    cardData.status == ActivityDetailStatus.toReceive
  ) {
    button = t("common.claim");
  } else if (
    cardData.details?.drawable_times &&
    cardData.details?.max_drawable_times
  ) {
    button = t("common.claim");
  }
  const list = cardData.details?.my_cards || [];

  const other = {
    count: list.reduce((res: number, cur: any) => {
      res += cur.num;
      return res;
    }, 0),
  };
  const text = [
    {
      title: t("bonus.task_completed"),
      value: cardData.details?.receivable_task_count + "",
    },
    {
      title: t("bonus.gacha_times"),
      value: cardData.details?.drawable_times + "",
    },
  ];

  let readyList: ReadyItem[] = [];
  const readyItem = getReadyItem(cardData, { button: t("common.claim") });
  readyList = readyItem ? [readyItem] : [];
  return {
    ...cardData,
    button,
    readyList: readyList,
    other,
    text,
  };
};

/** VIP日投注返利 */
const processVipRebateCardData = (
  cardData: BonusCardData,
  configData: ActivityCfg,
): BonusCardData => {
  cardData.title = t("vip.daily_cashback");
  cardData.bg_mode = BonusBgMode.green;
  cardData.vip = true;
  cardData.bg = isMobile()
    ? getImgurl("h5_bj_1.jpg", "bonusCenter/cardBg")
    : getImgurl("pc_bj_1.jpg", "bonusCenter/cardBg");

  if (cardData.hide) return cardData;

  const vip = userStore.userInfo?.UserVip?.vip_level || 0;
  const unLockVip = cardData.details_status?.un_lock_lv || 0;
  const lock = cardData.status == ActivityDetailStatus.Locked;
  let button = t("bonus.view_vip_club");
  const status = cardData.status;
  if (status == ActivityDetailStatus.canComplete) {
    button = t("common.claim");
  } else if (status == ActivityDetailStatus.canReceive) {
    button = t("common.claim");
  } else if (status == ActivityDetailStatus.toReceive) {
    button = t("common.claim");
  }
  let other = cardData.details || {};
  if (lock) {
    button =
      unLockVip > 0
        ? t("vip.unlock_val", { val: unLockVip })
        : t("bonus.details");
    const item = configData.cfg.vip_cfg?.find(
      (el: any) => el.vip_level == unLockVip,
    ); // 获取当前VIP的配置
    if (item) {
      const min_flow = item.min_flow;
      let flow_pro_1 = 0;
      const flow_map: any = {};
      for (const key in item.game_reward_config) {
        const cur = item.game_reward_config[key];
        const reward = cur.reward || { min_flow: 0, flow_pro: 0 };
        const { flow_pro } = reward;
        if (flow_pro && (flow_pro_1 == 0 || flow_pro > flow_pro_1)) {
          flow_pro_1 = flow_pro;
        }
        flow_map[cur.game_class] = { flow: 0, flow_pro: flow_pro, point: 0 };
      }

      other = {
        end_time: 0,
        rebate_data: {
          a_type: ActivityTypeEnum.vipRebate,
          flow: 0,
          flow_map: flow_map,
          flow_pro: flow_pro_1,
          min_flow,
          point: 0,
        },
      };
    }
  }
  const readyList: ReadyItem[] = [];
  if (cardData.rewards?.list?.length) {
    // 可领取
    cardData.rewards.list.forEach((el: any) => {
      const readyItem = getReadyItem(cardData, {
        button: t("common.claim"),
        type: 2,
        receive_point: el.point,
        id: el.id,
      });
      if (readyItem) {
        readyList.push(readyItem);
      }
    });
  } else if (cardData.status == ActivityDetailStatus.toReceive) {
    // 待领取
    const readyItem = getReadyItem(cardData, {
      button: t("common.claim"),
      type: 2,
      receive_point: cardData.details_status?.current_point || 0,
      id: "",
    });
    if (readyItem) {
      readyList.push(readyItem);
    }
  }

  const text = [];

  const value =
    other?.rebate_data?.flow_pro > 0
      ? `${(other?.rebate_data?.flow_pro / 100).toFixed(2)}%`
      : "-";
  text.push({
    title: t("bonus.rate"),
    value,
    info: other?.rebate_data?.flow_pro > 0,
  });

  const value1 =
    cardData.status != ActivityDetailStatus.Locked
      ? `${renderInnerHtml(
          formatToBindPoint({
            value: other?.rebate_data?.flow,
            isConverted: false,
            isReward: true,
          }),
          "text-general-light",
        )} / ${renderInnerHtml(
          formatToBindPoint({
            value: other?.rebate_data?.min_flow,
            isConverted: false,
            isReward: true,
            showSymbol: false,
          }),
          "text-general-bright",
        )}`
      : `${formatToBindPoint({
          value: other?.rebate_data?.min_flow,
          isConverted: false,
          isReward: true,
        })}`;
  if (other?.rebate_data) {
    text.push({
      title: t("bonus.wager"),
      value: value1,
    });
  }

  if (other?.rebate_data?.point) {
    text.push({
      title: t("bonus.expected_reward"),
      value: other.rebate_data.point
        ? `${formatToBindPoint({
            value: other.rebate_data.point,
            isConverted: false,
            isReward: true,
          })}`
        : "-",
    });
  }

  return {
    ...cardData,
    button,
    other,
    readyList,
    text,
  };
};

/** VIP亏损返现 */
const processVipLossRebateCardData = (
  cardData: BonusCardData,
  configData: ActivityCfg,
): BonusCardData => {
  cardData.title = t("vip.weekly_cashback");
  cardData.bg_mode = BonusBgMode.green;
  cardData.vip = false;
  cardData.bg = isMobile()
    ? getImgurl("h5_bj_1.jpg", "bonusCenter/cardBg")
    : getImgurl("pc_bj_3.jpg", "bonusCenter/cardBg");

  if (cardData.hide) return cardData;

  let button = t("bonus.view_vip_club");
  const vip = userStore.userInfo?.UserVip?.vip_level || 0;

  const unLockVip = cardData.details_status?.un_lock_lv || 0;
  const lock = cardData.status == ActivityDetailStatus.Locked;
  let show = useVipActivity().findVipLossRebate(vip);
  if (!show?.reward?.flow_pro) {
    show = configData.cfg.vip_cfg?.find(
      (el: any) => el.reward && el.reward.flow_pro,
    );
  }
  let other: any = show || {};
  const details = cardData.details || {};
  const currentTimestamp = Math.floor(Date.now() / 1000);

  // 创建新的 details 对象而不是直接修改
  const updatedDetails = {
    ...details,
    end_time:
      details.end_time < currentTimestamp
        ? details.end_time + 7 * 24 * 60 * 60
        : details.end_time,
  };

  if (cardData.status == ActivityDetailStatus.canReceive) {
    //当前有可领取奖励
    button = t("common.claim");
  } else if (lock) {
    button =
      unLockVip > 0
        ? t("vip.unlock_val", { val: unLockVip })
        : t("bonus.details");
    other = {
      ...other,
      max_reward_rate: updatedDetails.max_reward_rate,
    };
  } else if (
    updatedDetails?.receive_start_time <= currentTimestamp &&
    updatedDetails?.receive_end_time > currentTimestamp
  ) {
    //当前在亏损返现领取时间
    button = t("bonus.details");
  }

  const readyList: ReadyItem[] = [];
  if (cardData?.rewards?.list?.length) {
    cardData.rewards.list.forEach((el: any) => {
      const readyItem = getReadyItem(cardData, {
        button: t("common.claim"),
        type: 2,
        receive_point: el.point,
        id: el.id,
      });
      if (readyItem) {
        readyList.push(readyItem);
      }
    });
  } else if (cardData.status == ActivityDetailStatus.toReceive) {
    const readyItem = getReadyItem(cardData, {
      button: t("common.claim"),
      type: 2,
      receive_point: cardData.details_status?.current_point || 0,
      id: "",
    });
    if (readyItem) {
      readyList.push(readyItem);
    }
  }

  const text = [];

  const value =
    other?.max_reward_rate || other?.reward?.flow_pro
      ? `${((other?.max_reward_rate || other?.reward?.flow_pro) / 100).toFixed(2)}%`
      : "-";
  text.push({
    title: t("bonus.rate"),
    value,
  });

  const value1 = cardData?.details?.next_week_point
    ? `${formatToBindPoint({
        value: cardData.details.next_week_point,
        isConverted: false,
        isReward: true,
      })}`
    : "-";

  text.push({
    title: t("bonus.expected_reward"),
    value: value1,
  });

  return {
    ...cardData,
    details: updatedDetails,
    button,
    other: {
      ...other,
      rebate_data: { point: cardData.details.next_week_point },
    },
    readyList,
    text,
  };
};

/** VIP每日首充活动 */
const processVipDailyDepositBonusCardData = (
  cardData: BonusCardData,
  configData: ActivityCfg,
): BonusCardData => {
  const updatedCardData = {
    ...cardData,
    title: t("bonus.daily_first_deposit"),
    bg: isMobile()
      ? getImgurl("h5_bj_5.jpg", "bonusCenter/cardBg")
      : getImgurl("pc_bj_5.jpg", "bonusCenter/cardBg"),
  };
  let button = t("bonus.details");
  if (cardData.status == ActivityDetailStatus.canReceive) {
    button = t("common.claim");
  }
  // const vip = userStore.userInfo?.UserVip?.vip_level || 0;
  if (cardData.status == ActivityDetailStatus.canComplete) {
    button = t("common.deposit");
  }
  const lock = updatedCardData.status == ActivityDetailStatus.Locked;

  const unLockVip = updatedCardData.details_status?.un_lock_lv || 0;
  if (lock) {
    button =
      unLockVip > 0
        ? t("vip.unlock_val", { val: unLockVip })
        : t("bonus.details");
  }
  const mode = updatedCardData.details?.max_reward_point > 0 ? 1 : 2;
  const maxBonus = updatedCardData.details?.max_reward_point
    ? updatedCardData.details?.max_reward_point
    : updatedCardData.details?.max_pay_pro;

  const readyList: ReadyItem[] = [];

  if (updatedCardData?.rewards?.list?.length) {
    updatedCardData.rewards.list.forEach((el: any) => {
      const readyItem = getReadyItem(updatedCardData, {
        button: t("common.claim"),
        type: 2,
        receive_point: el.point,
        id: el.id,
      });
      if (readyItem) {
        readyList.push(readyItem);
      }
    });
  } else if (updatedCardData.status == ActivityDetailStatus.toReceive) {
    const readyItem = getReadyItem(updatedCardData, {
      button: t("common.claim"),
      type: 2,
      receive_point: updatedCardData.details_status?.current_point || 0,
      id: "",
    });
    if (readyItem) {
      readyList.push(readyItem);
    }
  } else if (updatedCardData.status == ActivityDetailStatus.canComplete) {
    const readyItem = getReadyItem(updatedCardData, {
      button: t("common.deposit"),
      type: 1,
      id: "",
    });
    if (readyItem) {
      readyList.push(readyItem);
    }
  }
  const text =
    mode == 1
      ? t("bonus.daily_first_deposit_desc", {
          val: formatToBindPoint({
            value: maxBonus,
            isConverted: false,
            isReward: true,
          }),
        })
      : t("bonus.daily_first_deposit_desc", {
          val: `${(maxBonus / 100).toFixed(2)}%`,
        });
  return {
    ...updatedCardData,
    button,
    readyList,
    text,
  };
};

const processShakeRedPacketBonusCardData = (
  cardData: BonusCardData,
  configData: ActivityCfg,
): BonusCardData => {
  const timeOffset = sysConfigStore.timeOffset;
  const updatedCardData = {
    ...cardData,
    title: t("red_envelope_rain.red_envelope"),
    bg: isMobile()
      ? getImgurl("h5_bj_7.jpg", "bonusCenter/cardBg")
      : getImgurl("pc_bj_7.jpg", "bonusCenter/cardBg"),
  };
  let button = "";
  let readyList: ReadyItem[] = [];
  // const status = cardData.details.is_active
  //   ? ActivityDetailStatus.canReceive
  //   : ActivityDetailStatus.check;
  const status = cardData.status;
  if (
    cardData.status === ActivityDetailStatus.canReceive ||
    cardData.status === ActivityDetailStatus.canComplete
  ) {
    button = t("common.claim");
    const readyItem = {
      type: 1,
      atype: updatedCardData.atype,
      img: updatedCardData.active_img || updatedCardData.img,
      order: updatedCardData.order,
      title: updatedCardData.title,
      button: t("common.claim"),
      status: cardData.status,
      receive_point: 0,
      id: 0,
    };
    readyList = readyItem ? [readyItem] : [];
  }
  let text: any[] = [];
  let result;
  if (isInSpecialPeriod(timeOffset)) {
    result = checkTimeInPeriods(
      timeOffset,
      configData?.cfg?.special_time_ranges || [],
    );
    text = [
      {
        title: t("red_envelope_rain.next_session"),
        value: result?.periodLabel,
      },
      {
        title: t("transaction.deposit_created"),
        value: result?.progress,
      },
    ];
  } else {
    result = checkTimeInPeriods(timeOffset, configData?.cfg?.time_ranges || []);
    text = [
      {
        title: t("red_envelope_rain.next_session"),
        value: result?.periodLabel,
      },
      {
        title: t("transaction.deposit_created"),
        value: result?.progress,
      },
    ];
  }

  return {
    ...updatedCardData,
    status,
    button,
    readyList,
    text,
  };
};

/** VIP周奖金 */
const processVipWeeklyBonusCardData = (
  cardData: BonusCardData,
  configData: ActivityCfg,
): BonusCardData => {
  const updatedCardData = {
    ...cardData,
    title: t("bonus.weekly_points"),
    bg: isMobile()
      ? getImgurl("h5_bj_5.jpg", "bonusCenter/cardBg")
      : getImgurl("pc_bj_5.jpg", "bonusCenter/cardBg"),
  };
  if (updatedCardData.hide) return updatedCardData;
  let button = t("bonus.view_vip_club");
  const lock = updatedCardData.status == ActivityDetailStatus.Locked;
  const vip = userStore.userInfo?.UserVip?.vip_level || 0;
  let show = useVipActivity().findVipWeekly(vip);
  if (!show?.reward?.point) {
    const list = configData.cfg?.vip_cfg || [];
    show = list.find((el: any) => el.reward && el.reward.point);
  }
  const unLockVip = updatedCardData.details_status?.un_lock_lv || 0;
  if (lock) {
    button =
      unLockVip > 0
        ? t("vip.unlock_val", { val: unLockVip })
        : t("bonus.details");
  }
  if (cardData.status == ActivityDetailStatus.canReceive) {
    button = t("common.claim");
  }
  // const vip = userStore.userInfo?.UserVip?.vip_level || 0;
  if (cardData.status == ActivityDetailStatus.canComplete) {
    button = t("common.deposit");
  }
  const readyList: ReadyItem[] = [];
  if (updatedCardData?.rewards?.list?.length) {
    updatedCardData.rewards.list.forEach((el: any) => {
      const readyItem = getReadyItem(updatedCardData, {
        button: t("common.claim"),
        type: 2,
        receive_point: el.point,
        id: el.id,
      });
      if (readyItem) {
        readyList.push(readyItem);
      }
    });
  } else if (updatedCardData.status == ActivityDetailStatus.toReceive) {
    const readyItem = getReadyItem(updatedCardData, {
      button: t("common.claim"),
      type: 2,
      receive_point: cardData.details_status?.current_point || 0,
      id: "",
    });
    if (readyItem) {
      readyList.push(readyItem);
    }
  }

  const text = [];
  const value =
    updatedCardData.status != ActivityDetailStatus.Locked
      ? `${renderInnerHtml(
          formatToBindPoint({
            value: updatedCardData.details?.week_bet_point,
            isConverted: false,
            isReward: true,
          }),
          "text-general-light",
        )} / ${renderInnerHtml(
          formatToBindPoint({
            value: show?.need_flow,
            isConverted: false,
            isReward: true,
            showSymbol: false,
          }),
          "text-general-bright",
        )}`
      : `${formatToBindPoint({
          value: show?.need_flow,
          isConverted: false,
          isReward: true,
        })}`;

  text.push({
    title: t("bonus.wager"),
    value,
  });
  const value1 = show?.reward?.point
    ? formatToBindPoint({
        value: show?.reward.point,
        isConverted: false,
        isReward: true,
      })
    : "-";
  text.push({
    title: t("bonus.weekly_points"),
    value: value1,
  });
  return {
    ...updatedCardData,
    button,
    readyList,
    text,
  };
};

/** VIP月奖金 */
const processVipMonthBonusCardData = (
  cardData: BonusCardData,
  configData: ActivityCfg,
): BonusCardData => {
  const updatedCardData = {
    ...cardData,
    title: t("vip.monthlypoints"),
    bg_mode: BonusBgMode.green,
    vip: true,
    bg: isMobile()
      ? getImgurl("h5_bj_5.jpg", "bonusCenter/cardBg")
      : getImgurl("pc_bj_5.jpg", "bonusCenter/cardBg"),
  };

  if (updatedCardData.hide) return updatedCardData;

  let button = t("bonus.view_vip_club");
  const lock = updatedCardData.status == ActivityDetailStatus.Locked;
  const unLockVip = updatedCardData.details_status?.un_lock_lv || 0;
  const vip = userStore.userInfo?.UserVip?.vip_level || 0;
  let show = useVipActivity().findVipMonthBonus(vip);
  if (!show?.reward?.point) {
    const list = configData?.cfg?.vip_cfg || [];
    show = list.find((el: any) => el.reward && el.reward.point);
  }
  if (lock) {
    button =
      unLockVip > 0
        ? t("vip.unlock_val", { val: unLockVip })
        : t("bonus.details");
  }
  if (updatedCardData.status == ActivityDetailStatus.canReceive) {
    button = t("common.claim");
  } else if (updatedCardData.status == ActivityDetailStatus.toReceive) {
    button = t("common.claim");
  } else if (updatedCardData.status == ActivityDetailStatus.canComplete) {
    button = t("common.claim");
  }

  const readyList: ReadyItem[] = [];
  if (updatedCardData?.rewards?.list?.length) {
    updatedCardData.rewards.list.forEach((el: any) => {
      const readyItem = getReadyItem(updatedCardData, {
        button: t("common.claim"),
        type: 2,
        receive_point: el.point,
        id: el.id,
      });
      if (readyItem) {
        readyList.push(readyItem);
      }
    });
  } else if (updatedCardData.status == ActivityDetailStatus.toReceive) {
    const readyItem = getReadyItem(updatedCardData, {
      button: t("common.claim"),
      type: 2,
      receive_point: cardData.details_status?.current_point || 0,
      id: "",
    });
    if (readyItem) {
      readyList.push(readyItem);
    }
  }

  const other: any = show || {};

  const text = [];

  const value =
    updatedCardData.status != ActivityDetailStatus.Locked
      ? `${renderInnerHtml(
          formatToBindPoint({
            value: updatedCardData.details?.month_bet_point,
            isConverted: false,
            isReward: true,
          }),
          "text-general-light",
        )} / ${renderInnerHtml(
          formatToBindPoint({
            value: other?.last_month_min_bet_point,
            isConverted: false,
            isReward: true,
            showSymbol: false,
          }),
          "text-general-bright",
        )}`
      : `${formatToBindPoint({
          value: other?.last_month_min_bet_point,
          isConverted: false,
          isReward: true,
        })}`;

  text.push({
    title: t("bonus.wager"),
    value,
  });
  const value1 = other.reward?.point
    ? formatToBindPoint({
        value: other.reward.point,
        isConverted: false,
        isReward: true,
      })
    : "-";
  text.push({
    title: t("vip.monthlypoints"),
    value: value1,
  });

  return {
    ...updatedCardData,
    button,
    readyList,
    other,
    text,
  };
};

/** 周体育投注返利 */
const processWeeklyRebateCardData = (
  cardData: BonusCardData,
  configData: ActivityCfg,
): BonusCardData => {
  const updatedCardData = {
    ...cardData,
    title: t("vip.sports_weekly_cashback"),
    bg_mode: BonusBgMode.green,
    vip: true,
    bg: isMobile()
      ? getImgurl("h5_bj_3.jpg", "bonusCenter/cardBg")
      : getImgurl("pc_bj_3.jpg", "bonusCenter/cardBg"),
  };

  if (updatedCardData.hide) return updatedCardData;

  let button = t("bonus.view_vip_club");
  const lock = updatedCardData.status == ActivityDetailStatus.Locked;
  const unLockVip = updatedCardData.details_status?.un_lock_lv || 0;
  const vip = userStore.userInfo?.UserVip?.vip_level || 0;

  let other: any = updatedCardData.details || {};
  const status = updatedCardData.status;

  const readyList: ReadyItem[] = [];
  if (updatedCardData?.rewards?.list?.length) {
    updatedCardData.rewards.list.forEach((el: any) => {
      const readyItem = getReadyItem(updatedCardData, {
        button: t("common.claim"),
        type: 2,
        receive_point: el.point,
        id: el.id,
      });
      if (readyItem) {
        readyList.push(readyItem);
      }
    });
  } else if (updatedCardData.status == ActivityDetailStatus.toReceive) {
    const readyItem = getReadyItem(updatedCardData, {
      button: t("common.claim"),
      type: 2,
      receive_point: cardData.details_status?.current_point || 0,
      id: "",
    });
    if (readyItem) {
      readyList.push(readyItem);
    }
  }
  if (updatedCardData.status == ActivityDetailStatus.canReceive) {
    button = t("common.claim");
  }
  if (lock) {
    button =
      unLockVip > 0
        ? t("vip.unlock_val", { val: unLockVip })
        : t("bonus.details");
    const item = configData.cfg.vip_cfg?.find(
      (el: any) => el.vip_level == unLockVip,
    );
    if (item) {
      const min_flow = item.min_flow;
      let flow_pro_1 = 0;
      const flow_map: any = {};
      for (const key in item) {
        const cur = item[key];
        const reward = cur.reward || { min_flow: 0, flow_pro: 0 };
        const { flow_pro } = reward;

        if (flow_pro && (flow_pro_1 == 0 || flow_pro > flow_pro_1)) {
          flow_pro_1 = flow_pro;
        }
        if (typeof cur === "object") {
          flow_map[cur.game_class] = { flow: 0, flow_pro: flow_pro, point: 0 };
        }
      }
      other = {
        end_time: 0,
        rebate_data: {
          a_type: ActivityTypeEnum.weeklyRebate,
          flow: 0,
          flow_map: flow_map,
          flow_pro: flow_pro_1,
          min_flow,
          point: 0,
        },
      };
    }
  }
  const text = [];
  const value = other?.rebate_data?.flow_pro
    ? `${(other?.rebate_data?.flow_pro / 100).toFixed(2)}%`
    : "-";
  text.push({
    title: t("bonus.rate"),
    value,
    info: other?.rebate_data?.flow_pro > 0,
  });
  const value1 = other?.rebate_data
    ? `${renderInnerHtml(
        formatToBindPoint({
          value: other.rebate_data.flow,
          isConverted: false,
          isReward: true,
        }),
        "text-general-light",
      )} / ${renderInnerHtml(
        formatToBindPoint({
          value: other.rebate_data.min_flow,
          isConverted: false,
          isReward: true,
          showSymbol: false,
        }),
        "text-general-bright",
      )}`
    : `${renderInnerHtml(
        formatToBindPoint({
          value: other.rebate_data.min_flow,
          isConverted: false,
          isReward: true,
        }),
        "text-general-bright",
      )}`;
  text.push({
    title: t("bonus.wager"),
    value: value1,
  });

  text.push({
    title: t("bonus.expected_reward"),
    value: other.rebate_data.point
      ? `${formatToBindPoint({
          value: other.rebate_data.point,
          isConverted: false,
          isReward: true,
        })}`
      : "-",
  });
  return {
    ...updatedCardData,
    button,
    other,
    readyList,
    status,
    text,
  };
};

/** 道具列表 */
const processPropsItemCardData = (cardData: BonusCardData): BonusCardData => {
  cardData.title = t("reward.items");
  cardData.bg_mode = BonusBgMode.green;
  cardData.vip = false;
  cardData.bg = isMobile()
    ? getImgurl("h5_bj_6.jpg", "bonusCenter/cardBg")
    : getImgurl("pc_bj_6.jpg", "bonusCenter/cardBg");
  cardData.status = ActivityDetailStatus.check;
  cardData.button = t("bonus.details");
  cardData.hide = false;
  cardData.img = getImgurl("100000000086.png?231", "bonusCenter/cardIcon2");
  cardData.atype = ActivityTypeEnum.propsItem;
  cardData.order = 10000;

  let button = t("home.contest_details");
  if (propsStore.unuse.length > 0) {
    cardData.status = ActivityDetailStatus.canComplete;
    button = t("item.use");
  }
  const readyList: ReadyItem[] = [];
  const readyItem = getReadyItem(cardData, {
    button: t("item.use"),
    id: 0,
  });
  if (readyItem) {
    readyList.push(readyItem);
  }

  const text = [];
  text.push({
    title: t("bonus.not_used"),
    value: propsStore.unuse.length + "",
  });
  text.push({
    title: t("bonus.in_use"),
    value: propsStore.inuse.length + "",
  });

  return {
    ...cardData,
    text,
    readyList: readyList,
    button,
  };
};

/** 投注比赛活动奖金中心 */
const processWagerContestCardData = (
  cardData: BonusCardData,
): BonusCardData => {
  cardData.title = t("bonus.category_62");
  cardData.bg_mode = BonusBgMode.green;
  cardData.vip = true;
  cardData.bg = isMobile()
    ? getImgurl("h5_bj_1.jpg", "bonusCenter/cardBg")
    : getImgurl("pc_bj_1.jpg", "bonusCenter/cardBg");

  cardData.atype = ActivityTypeEnum.wagerContest;
  cardData.details = {};
  cardData.rewards = {};
  cardData.order = 9999;

  cardData.img = getImgurl("626364.png?231", "bonusCenter/cardIcon2");
  cardData.hide = true;
  cardData.button = t("common.details");
  cardData.status = ActivityDetailStatus.check;
  cardData.readyList = [];
  cardData.other = {
    rankingBonus: 0,
    rankingCode: 0,
    aTypes: [],
    settle_type: -1,
  };
  if (wagerTabList().length) {
    const aType = wagerTabList()[0].type;
    cardData.details = getActivityDetails(aType)?.details;
    cardData.hide = false;
    cardData.status = ActivityDetailStatus.check;
    cardData.details_status = {
      countdown: 0,
      status: ActivityDetailStatus.check,
      new_status: ActivityDetailStatus.check,
      un_lock_lv: 0,
      current_point: 0,
    };
    cardData.details_status.countdown =
      getActivityCfg(aType)?.cfg?.last_time || 0;
    if (cardData.other.settle_type == -1) {
      cardData.other.settle_type = getActivityCfg(aType)?.cfg?.settle_type;
    }
  }
  const list = Object.values(bonusCenterStore.bonusCenterCardData)?.filter(
    (item) => wagerContestActList.includes(item.atype),
  );
  let button = t("common.details");
  for (let i = 0; i < list.length; i++) {
    const item = list[i];
    if (item.rewards?.list?.length) {
      const reward = item.rewards.list.reduce(
        (res: any, cur: any) => {
          res.point += cur.point || 0;
          res.code += cur.code || 0;
          return res;
        },
        { point: 0, code: 0 },
      );
      const other = cardData.other || {};
      other.rankingBonus += reward.point;
      other.rankingCode += reward.code;
      other.aTypes.push(item.atype);
      cardData.rewards.receive_point = other.rankingBonus;
      button = t("home.contest_details");
      cardData.status = ActivityDetailStatus.canReceive;
      cardData.hide = false;
    }
  }
  const text = t("bonus.win_ranking_rewards");

  const readyItem = getReadyItem(cardData, { button: t("common.claim") });
  cardData.readyList = readyItem ? [readyItem] : [];
  return {
    ...cardData,
    button: button,
    text,
  };
};

/** 新人七日任务 */
const process7DayNewbieTaskCardData = (
  cardData: BonusCardData,
): BonusCardData => {
  const updatedCardData = {
    ...cardData,
    title: t("bonus.quest"),
    bg_mode: BonusBgMode.green,
    bg: isMobile()
      ? getImgurl("h5_bj_1.jpg", "bonusCenter/cardBg")
      : getImgurl("pc_bj_1.jpg", "bonusCenter/cardBg"),
  };
  if (updatedCardData.hide) return updatedCardData;
  // 额外处理是否隐藏结束
  let button = t("bonus.details");
  if (updatedCardData.status == ActivityDetailStatus.canReceive) {
    button = t("common.claim");
  }
  const text = [];
  const completedTaskNum = updatedCardData.details?.completed_task_num || 0;
  const totalTaskNum = updatedCardData.details?.task_num || 0;
  const day = cardData.details?.current_day;
  if (day < 8) {
    text.push({
      title: t("bonus.quest_progress"),
      value: `${renderInnerHtml(completedTaskNum, "text-general-light")} / ${renderInnerHtml(totalTaskNum, "text-general-bright")}`,
    });
    text.push({
      title: t("bonus.claimable"),
      value: formatToBindPoint({
        value: updatedCardData.details?.tot_reward_point || 0,
        isConverted: false,
        isReward: true,
      }),
    });
  } else {
    text.push({
      title: t("sevendays.newbie_pack"),
      value: formatToBindPoint({
        value: updatedCardData.details?.end_reward_point || 0,
        isConverted: false,
        isReward: true,
      }),
    });
  }

  let readyList: ReadyItem[] = [];
  const readyItem = getReadyItem(updatedCardData, {
    button: t("common.claim"),
    type: 1,
  });
  readyList = readyItem ? [readyItem] : [];
  return {
    ...updatedCardData,
    button,
    readyList: readyList,
    text,
  };
};

/** 幸运转盘 */
const processRouletteCardData = (cardData: BonusCardData): BonusCardData => {
  const updatedCardData = {
    ...cardData,
    title: t("reward.luckywheel"),
    bg_mode: BonusBgMode.green,
    vip: false,
    bg: isMobile()
      ? getImgurl("h5_bj_2.jpg", "bonusCenter/cardBg")
      : getImgurl("pc_bj_2.jpg", "bonusCenter/cardBg"),
  };
  if (updatedCardData.hide) return updatedCardData;
  // 额外处理是否隐藏结束
  let button = t("bonus.details");
  if (updatedCardData.status == ActivityDetailStatus.canReceive) {
    button = t("common.spin");
  }

  const text: any[] = [];
  text.push({
    title: t("bonus.spins"),
    value: updatedCardData.details?.ticket_count + "",
  });
  // 幸运转盘无倒计时状态不显示
  // text.push({
  //   title: t("bonus.next_spin_time"),
  //   countDown: true,
  //   endTime: updatedCardData.details_status.countdown,
  //   value: "00:00:00",
  // });
  let readyList: ReadyItem[] = [];
  const readyItem = getReadyItem(updatedCardData, {
    button: t("common.spin"),
  });
  readyList = readyItem ? [readyItem] : [];
  return {
    ...updatedCardData,
    button,
    readyList: readyList,
    text,
  };
};

const getFirstDepositMaxReward = () => {
  const { actConfigData } = activityStore;
  const firstDepositCfg = actConfigData[ActivityTypeEnum.firstDeposit];
  if (!firstDepositCfg) return 0;
  const list: any[] = firstDepositCfg.cfg?.list || [];
  const mode = list.length ? list[0].reward?.reward_point_mode : 0;
  if (mode === 1) {
    const result = list.reduce((pre, item) => {
      if (item?.reward?.point > pre) {
        pre = item?.reward?.point;
      }
      return pre;
    }, 0);
    return formatToBindPoint({
      value: result,
      isConverted: false,
      isReward: true,
    });
  } else {
    const result = list.reduce((pre, item) => {
      if (item?.reward?.pay_pro > pre) {
        pre = item?.reward?.pay_pro;
      }
      return pre;
    }, 0);
    return (result / 100).toFixed(2) + "%";
  }
};

/** 新首充活动 */
const processFirstDepositCardData = (
  cardData: BonusCardData,
): BonusCardData => {
  const updatedCardData = {
    ...cardData,
    title: t("bonus.deposit_bonus"),
    bg_mode: BonusBgMode.green,
    vip: false,
    bg: isMobile()
      ? getImgurl("h5_bj_5.jpg", "bonusCenter/cardBg")
      : getImgurl("pc_bj_5.jpg", "bonusCenter/cardBg"),
  };

  /* prettier-ignore */
  if (!updatedCardData.details || (updatedCardData.details?.today_deposit || 0) >= (updatedCardData.details?.min_deposit || 0)) {
    updatedCardData.hide = true;
  }

  if (updatedCardData.hide) return updatedCardData;
  // 额外处理是否隐藏结束
  let button = t("common.deposit");
  if (updatedCardData.status == ActivityDetailStatus.canReceive) {
    button = t("common.claim");
  }

  const text = t("bonus.deposit_bonus_rules", {
    val1: getFirstDepositMaxReward(),
  });
  let readyList: ReadyItem[] = [];
  const readyItem = getReadyItem(updatedCardData, {
    button: t("common.claim"),
  });
  readyList = readyItem ? [readyItem] : [];
  return {
    ...updatedCardData,
    button,
    readyList: readyList,
    text,
  };
};

const processFlipCardCardData = (cardData: BonusCardData): BonusCardData => {
  const updatedCardData = {
    ...cardData,
    bg_mode: BonusBgMode.red,
    title: t("bonus.flip_card"),
    bg: isMobile()
      ? getImgurl("h5_bj_7.jpg", "bonusCenter/cardBg")
      : getImgurl("pc_bj_7.jpg", "bonusCenter/cardBg"),
  };

  let buttonText = "";
  const flipCount = cardData?.details?.ticket_num || 0;
  const keyRewards = cardData?.details?.key_rewards || [];

  if (flipCount > 0) {
    buttonText = t("flip_card.flip");
  }

  const isReceivable = keyRewards?.some((el: any) => {
    return el.receivable_at * 1000 < Date.now();
  });

  if (isReceivable) {
    buttonText = t("common.claim");
  }

  let readyList: ReadyItem[] = [];
  const readyItem = getReadyItem(updatedCardData, {
    type: 1, // 翻卡活动特殊处理不要有 奖金内容
    button: buttonText,
  });
  readyList = readyItem ? [readyItem] : [];

  const text = [
    {
      title: t("flip_card.diamond"),
      value: `${cardData.details?.diamond_num || 0} / 3`,
    },
    {
      title: t("flip_card.key"),
      value: `${cardData.details?.key_num || 0} / 3`,
    },
    {
      title: t("flip_card.flip_count"),
      value: cardData.details?.ticket_num || 0,
    },
  ];
  // mx and br special path
  const path = isAfun() ? "afun" : "";

  return {
    ...updatedCardData,
    // 状态只有 0,2,3, 状态则 遇 0 改为 4 (让按钮有作用)
    status: cardData.status || ActivityDetailStatus.check,
    readyList: readyList,
    button: buttonText,
    text,
    img: getImgurl(`92_2.png?10086`, `bonusCenter/cardIcon2/${path}`),
  };
};

const processDailyCashbackExperienceCardData = (
  cardData: BonusCardData,
  configData: ActivityCfg,
) => {
  // 原型：https://38h3zb.axshare.com/?id=tey8j6&p=h5-2_&sc=3

  // -- 前置处理
  cardData.title = t("daily_cashback.experience_title");
  cardData.bg_mode = BonusBgMode.green;
  cardData.vip = false;
  cardData.bg = isMobile()
    ? getImgurl("h5_bj_8.jpg?v=202511011640", "bonusCenter/cardBg")
    : getImgurl("pc_bj_8.jpg?v=202511011640", "bonusCenter/cardBg");

  if (cardData.hide) return cardData;

  // -- 卡片按钮文案
  const buttonText = t("bonus.details");

  // -- 卡片信息列表数据
  const text = [];
  // -- no.1：返水比例：根据后台配置，取当前挡位返水比例最高的数值
  const game_rates = configData.cfg.game_rates;
  let rebateRate = "0%";
  if (game_rates) {
    const ratios = Object.values(game_rates);
    const maxRatio = Math.max(...ratios);
    rebateRate = `${maxRatio / 100}%`;
  }
  text.push({
    title: t("daily_cashback.rebate_rate"),
    value: rebateRate,
    info: true,
  });
  // -- no.2：参与天数
  const participationDays =
    cardData.status === ActivityDetailStatus.Finish
      ? t("daily_cashback.ended")
      : `${cardData.details.registration_days}/${cardData.details.valid_days}${t("common.day")}`;
  text.push({
    title: t("daily_cashback.participation_days"),
    value: participationDays,
  });
  // -- no.3：预期奖励
  if (cardData.status !== ActivityDetailStatus.Finish) {
    const expectedReward = formatToBindPoint({
      value: cardData.details.rebate_data.point,
      isConverted: false,
      isReward: true,
    });
    text.push({
      title: t("daily_cashback.expected_reward"),
      value: expectedReward,
    });
  }

  // -- 信息悬浮数据列表
  const flow_map = Object.fromEntries(
    Object.entries(game_rates || {}).map(([key, value]) => [
      key,
      { flow_pro: value },
    ]),
  );
  const other = {
    rebate_data: {
      a_type: ActivityTypeEnum.dailyCashbackExperience,
      flow_map,
    },
  };

  // -- 宝箱列表数据（Tips：93活动比较特殊，其他活动不要效仿这里的逻辑）
  const readyList: ReadyItem[] = [];
  // 1、计算领取奖励（可领取奖励 + 待领取奖励）
  const receive_point = (cardData.rewards?.list || []).reduce(
    (prev: number, curr: any) => prev + curr.point,
    cardData.details_status.current_point || 0,
  );
  // 2. 构造宝箱列表数据
  if (receive_point) {
    const readyItem = getReadyItem(cardData, {
      button: t("common.claim"),
      type: 2, // type 2 表示宝箱列表要不要展示钱数，2表示要
      receive_point,
      status: cardData.details_status.new_status,
    });
    if (readyItem) readyList.push(readyItem);
  }

  const data = {
    ...cardData,
    button: buttonText,
    other,
    text,
    readyList,
  };
  return data;
};

// 优惠活动
const processCompetitionCardData = (
  cardData: BonusCardData,
  configData: ActivityCfg,
) => {
  // 原型：https://38h3zb.axshare.com/?id=tey8j6&p=h5-2_&sc=3

  // -- 前置处理
  cardData.title = t("tournament.title");
  cardData.bg_mode = BonusBgMode.green;
  cardData.vip = false;
  cardData.bg = isMobile()
    ? getImgurl("h5_bj_8.jpg?v=202511011640", "bonusCenter/cardBg")
    : getImgurl("pc_bj_8.jpg?v=202511011640", "bonusCenter/cardBg");

  if (cardData.hide) return cardData;

  // -- 卡片按钮文案
  const buttonText = t("bonus.details");

  // -- 卡片信息列表数据
  const text = [];

  const data = {
    ...cardData,
  };
  return data;
};
const processDailyLossBackCardData = (
  cardData: BonusCardData,
  configData: ActivityCfg,
): BonusCardData => {
  const updatedCardData = {
    ...cardData,
    title: t("reward.daily_lossback"),
    bg_mode: BonusBgMode.red,
    vip: false,
    bg: isMobile()
      ? getImgurl("h5_bj_7.jpg", "bonusCenter/cardBg")
      : getImgurl("pc_bj_7.jpg", "bonusCenter/cardBg"),
  };
  const text = [];

  // 当前亏损金额与下一档亏损金额
  text.push({
    title: t("bonus.loss_required"),
    value: `${formatToBindPoint({
      value: updatedCardData.details?.loss_point || 0,
    })} / ${formatToBindPoint({
      showSymbol: false,
      value: updatedCardData.details?.next_level_cfg?.loss_point || 0,
    })}`,
  });
  // 奖励比例
  text.push({
    title: t("bonus.rete"),
    value: `${(updatedCardData.details?.flow_pro / 100).toFixed(2)}%`,
    info: true,
  });
  // 预期奖励
  text.push({
    title: t("bonus.ready"),
    value:
      updatedCardData.details?.loss_point === 0
        ? "-"
        : formatToBindPoint({
            value: updatedCardData.details?.next_day_point || 0,
          }),
  });
  let other: any = {};
  const directMap: DirectMap = {};
  configData.cfg?.level_cfg?.forEach((item: any) => {
    directMap[
      `${formatToBindPoint({
        value: item.loss_point,
      })}`
    ] = {
      flow: item.reward_point_mode === 5,
      value: `${(item.reward.flow_pro / 100).toFixed(2)}%`,
    };
  });

  other = {
    rebate_data: {
      directShow: true,
      directMap,
    },
  };
  // 倒计时状态时 需要看到详情按钮 不显示倒计时 倒计时到页面中查看
  let status = cardData.details_status.new_status;
  let button = "";
  if (status === ActivityDetailStatus.countdown) {
    status = ActivityDetailStatus.check;
  }
  if (cardData.details_status.new_status === ActivityDetailStatus.canReceive) {
    button = t("common.claim");
  } else {
    button = t("bonus.details");
  }
  let readyList: ReadyItem[] = [];
  const readyItem = getReadyItem(updatedCardData, {
    button: t("common.claim"),
    receive_point: updatedCardData.details?.point || 0,
    id: updatedCardData.details?.id || 0,
  });
  readyList = readyItem ? [readyItem] : [];
  // let hide = false;
  // if (cardData.details_status.new_status === ActivityDetailStatus.Finish) {
  //   hide = true;
  // } else if (cardData.rewards?.list?.length) {
  //   hide = false;
  // } else if (
  //   !cardData.rewards?.list?.length &&
  //   cardData.details.end_time === 0
  // ) {
  //   hide = true;
  // }
  return {
    ...updatedCardData,
    button,
    status,
    readyList,
    text,
    other,
    img: getImgurl(`94.png?1103`, "bonusCenter/cardIcon2"),
    // hide,
  };
};

/** 下载活动 */
const processAppDownloadCardData = (cardData: BonusCardData): BonusCardData => {
  const updatedCardData = {
    ...cardData,
    title: t("home.download_app"),
    bg_mode: BonusBgMode.green,
    vip: false,
    bg: isMobile()
      ? getImgurl("h5_bj_5.jpg", "bonusCenter/cardBg")
      : getImgurl("pc_bj_5.jpg", "bonusCenter/cardBg"),
  };
  if (updatedCardData.hide) return updatedCardData;
  // 额外处理是否隐藏结束
  let button = t("install.download_button");
  if (updatedCardData.status == ActivityDetailStatus.canReceive) {
    button = t("common.claim");
  }
  const needDeposit = updatedCardData.details?.need_pay;
  const needBet = updatedCardData.details?.need_bet;
  const needBetValue = updatedCardData.details?.is_bet ? "1" : "0";
  const betValue2 = `${renderInnerHtml(needBetValue, "text-general-light")} / ${renderInnerHtml("1", "text-general-bright")}`;
  const deposit = updatedCardData.details?.is_pay ? "1" : "0";
  const value = `${renderInnerHtml(deposit, "text-general-light")} / ${renderInnerHtml("1", "text-general-bright")}`;
  const download = updatedCardData.details?.is_down_app ? "1" : "0";
  const value2 = `${renderInnerHtml(download, "text-general-light")} / ${renderInnerHtml("1", "text-general-bright")}`;
  const text = [];
  if (needDeposit) {
    text.push({
      title: t("bonus.first_deposit"),
      value: value,
    });
  }
  if (needBet) {
    text.push({
      title: t("bonus.anybet"),
      value: betValue2,
    });
  }
  text.push({
    title: t("bonus.app_login"),
    value: value2,
  });
  text.push({
    title: t("bonus.bonus"),
    value: formatToBindPoint({
      value: cardData.details?.point || 0,
      isConverted: false,
      isReward: true,
    }),
  });
  let readyList: ReadyItem[] = [];
  const readyItem = getReadyItem(updatedCardData, {
    button: t("common.claim"),
    type: 2,
    receive_point: cardData.details?.point || 0,
    id: cardData.details?.id || 0,
  });
  readyList = readyItem ? [readyItem] : [];
  return {
    ...updatedCardData,
    button,
    readyList: readyList,
    text,
  };
};

const processVipWeeklyCardCardData = (
  cardData: BonusCardData,
  configData: ActivityCfg,
): BonusCardData => {
  const updatedCardData = {
    ...cardData,
    title: t("super_value_card.card"),
    img: getImgurl(`75.png?1108955`, "bonusCenter/cardIcon2"),
    bg: isMobile()
      ? getImgurl("h5_bj_9.jpg", "bonusCenter/cardBg")
      : getImgurl("pc_bj_9.jpg", "bonusCenter/cardBg"),
  };
  if (updatedCardData.hide) return updatedCardData;
  let text: Record<string, string | boolean>[] | string = [];
  // 倒计时状态 支持查看活动内容
  let status = cardData.details_status.new_status;
  // 新增禁用购买状态判断 是否隐藏卡片
  const hide =
    configData.cfg?.disable_purchase || status === ActivityDetailStatus.Finish;
  // 如果禁用购买状态且没有奖励数据 则隐藏卡片
  if (hide && !cardData.details?.day_rewards) {
    updatedCardData.hide = true;
    return updatedCardData;
  }

  let readyList: ReadyItem[] = [];
  let readyItem: ReadyItem | null = null;
  let button = "";
  if (status === ActivityDetailStatus.countdown) {
    status = ActivityDetailStatus.check;
  }
  if (status === ActivityDetailStatus.canComplete) {
    button = t("common.claim");
  }
  if (cardData.details?.day_rewards) {
    text.push({
      title: t("bonus.counts"),
      value: `${cardData.details?.day} / ${cardData.details?.day_rewards.length}`,
    });

    // 任务进度
    const taskProgress = cardData.details?.day_rewards.find(
      (item: any) => item.day === cardData.details?.day,
    );
    const textCfg = {
      title: "",
      value: "",
    };
    if (taskProgress.limit_type === 1 || taskProgress.limit_type === 2) {
      textCfg.title =
        taskProgress.limit_type === 1
          ? t("bet.bet_amount")
          : t("affiliate.deposit");
      textCfg.value = `${formatToBindPoint({ value: taskProgress.value })} / ${formatToBindPoint({ value: taskProgress.limit_value })}`;
    } else if (taskProgress.limit_type === 3) {
      textCfg.title = t("bonus.invite_friends");
      textCfg.value = `${taskProgress.value} / ${taskProgress.limit_value}`;
    }
    if (textCfg.title) {
      text.push(textCfg);
    }
    // 获取当前卡片的奖励
    const dayReward =
      configData.cfg?.vip_cards
        ?.find((item: any) => item.id === cardData.details?.ticket?.card_id)
        ?.day_rewards?.find((item: any) => item.day === cardData.details?.day)
        ?.reward.point || 0;
    text.push({
      title: t("bonus.bonus"),
      value: formatToBindPoint({
        value: dayReward,
      }),
    });
    readyItem = getReadyItem(updatedCardData, {
      button: t("common.claim"),
      receive_point: dayReward,
      id: cardData.details?.ticket?.card_id || 0,
    });
    // if (status === ActivityDetailStatus.check) {
    //   button = t("common.details");
    // } else {
    //   button = t("common.claim");
    // }
  } else {
    // 未购买是显示的内容
    const calculateCardTotalValue = (data: ConfWeekCards) => {
      return (
        data.day_rewards?.reduce((acc, curr) => acc + curr.reward.point, 0) ?? 0
      );
    };
    const values = configData.cfg?.vip_cards?.map((item: ConfWeekCards) =>
      calculateCardTotalValue(item),
    );
    text = t("super_value_card.buy_illustrate", {
      val: formatToBindPoint({
        value: values ? Math.max(...values) : 0,
      }),
    }) as string;
    if (status === ActivityDetailStatus.check) {
      status = ActivityDetailStatus.canComplete;
      button = t("super_value_card.buy_card");
      updatedCardData.status = ActivityDetailStatus.canComplete;
    }
    readyItem = getReadyItem(updatedCardData, {
      button: t("super_value_card.buy_card"),
    });
  }

  readyList = readyItem ? [readyItem] : [];
  return {
    ...updatedCardData,
    status,
    text,
    readyList,
    button,
  };
};

const processCashFlopCardData = (
  cardData: BonusCardData,
  configData: ActivityCfg,
): BonusCardData => {
  const updatedCardData = {
    ...cardData,
    title: t("invite_wheel.invite_wheel"),
    img: getImgurl(`43.png?1108955`, "bonusCenter/cardIcon"),
    bg: isMobile()
      ? getImgurl("h5_bj_6.jpg", "bonusCenter/cardBg")
      : getImgurl("pc_bj_6.jpg", "bonusCenter/cardBg"),
  };
  if (updatedCardData.hide) return updatedCardData;
  const text: Record<string, string | boolean>[] | string = [];

  text.push({
    title: t("invite_wheel.spins_available"),
    value: updatedCardData.details?.count,
  });
  text.push({
    title: t("invite_wheel.progress"),
    value: `${formatToBindPoint({
      value: updatedCardData.details?.bonus || 0,
      isConverted: false,
      isReward: true,
    })} / ${formatToBindPoint({
      value: updatedCardData.details?.reward_point || 0,
      isConverted: false,
      isReward: true,
    })}`,
  });
  // 倒计时状态 支持查看活动内容
  let status = updatedCardData.details_status.new_status;
  const readyList: ReadyItem[] = [];
  let button = "";
  if (status === ActivityDetailStatus.countdown) {
    status = ActivityDetailStatus.check;
  }
  if (status === ActivityDetailStatus.canComplete) {
    button = t("invite_wheel.spins");
    const readyItem = getReadyItem(updatedCardData, {
      button: t("invite_wheel.spins"),
      type: 1,
    });
    if (readyItem) readyList.push(readyItem);
  }
  if (status === ActivityDetailStatus.canReceive) {
    button = t("common.claim");
    const receive_point = updatedCardData.details?.reward_point || 0;
    const readyItem = getReadyItem(updatedCardData, {
      button: t("common.receive"),
      type: 2,
      receive_point,
      status: updatedCardData.details_status.new_status,
    });
    if (readyItem) readyList.push(readyItem);
  }

  return {
    ...updatedCardData,
    status,
    text,
    readyList,
    button,
  };
};

export const doManualExchangeBonus = (
  manualBonusPointType: number,
  push: (path: string) => void,
) => {
  const reqFun = async (point_type: number) => {
    postDoManualExchangeBonus({ point_type: point_type })?.then(
      async (res: any) => {
        const point = res?.point; //这次彩金兑换的本金
        const { showDialog } = useDialog();
        if (!point) return;
        showDialog({
          type: DialogType.Reward,
          data: {
            items: [{ wallet: 0, point: point }],
          },
        });
        getUserInfo();
        // const point_type = res?.point_type //币种
      },
    );
  };
  const { showDialog, closeDialog } = useDialog();
  // );
  const item = manualBonusList()?.filter(
    (item: any) => item.pointType === manualBonusPointType,
  );
  if (item[0].completedCodingQuantity < item[0].requiredCodingQuantity) {
    if (isMobile()) {
      push("/wallet/rollover");
    } else {
      showDialog({ type: DialogType.BillFlow });
    }
  } else {
    const bet_profit_amount =
      redeemBonus().bonus - redeemBonus().max_exchangable_bonus;
    const value1 = formatToBindPoint({
      value: redeemBonus().max_exchangable_bonus,
      isConverted: false,
      isReward: true,
    });
    const value2 = formatToBindPoint({
      value: bet_profit_amount,
      isConverted: false,
      isReward: true,
    });
    const value3 = formatToBindPoint({
      value:
        bet_profit_amount *
        sysConfigStore.sysConfigData.exchange_over_bonus_code,
      isConverted: false,
      isReward: true,
    });

    const str = ` <div class='whitespace-normal text-general-bright leading-[20px]'>
              <p>${t("reward.redeem_success", { val: value1 })}</p>
    					<p>${t("reward.bonus_convert_hint", { val1: value2, val2: value3 })}</p>
    					<p>${t("reward.need_redeem")}</p>
    				</div>`;
    if (bet_profit_amount > 0) {
      showDialog({
        type: DialogType.Tips,
        data: {
          title: "Tips",
          content: str,
          cancelText: "Cancel",
          confirmText: "Confirm",
          onCancel: () => closeDialog(DialogType.Tips),
          onConfirm: () => reqFun(manualBonusPointType),
        },
      });
    } else {
      reqFun(manualBonusPointType);
    }
  }
};

export const manualBonusList = () => {
  // 彩金手动兑换，返回所有有彩金的币种
  const cryptoMaps = userStore.userInfo?.crypto_user_points || {};
  const manualExchangeBonus = userStore.userInfo?.manual_exchange_bonus;
  const list: any[] = [];
  if (!manualExchangeBonus) {
    return list;
  }
  for (const key in cryptoMaps) {
    const item = cryptoMaps[key];
    if (item.bonus > 0 && item.pointType == sysConfigStore.point_type) {
      const processedItem = {
        ...item,
        requiredCodingQuantity: item?.bonusCode || 0,
        completedCodingQuantity: item?.bonusCode_c || 0,
        currencyName: getCrypto(item.pointType)?.name,
      };

      list.push(processedItem);
    }
  }
  return list;
};

export const redeemBonus = () => {
  const item = manualBonusList().find(
    (item: any) => item.pointType == sysConfigStore.point_type,
  );
  return item || {};
};

// 活动处理映射
const activityProcessors = {
  [ActivityTypeEnum.reliefFund3]: processReliefFund3CardData,
  [ActivityTypeEnum.newCheckIn]: processNewCheckInCardData,
  [ActivityTypeEnum.dailyLossRebate]: processDailyLossRebateCardData,
  [ActivityTypeEnum.vipUpgrade]: processVipUpgradeCardData,
  [ActivityTypeEnum.vipWeekly]: processVipWeeklyBonusCardData,
  [ActivityTypeEnum.vipRebate]: processVipRebateCardData,
  [ActivityTypeEnum.gachapon]: processGachaponCardData,
  [ActivityTypeEnum.newbieWheel]: processNewbieWheelCardData,
  [ActivityTypeEnum.superValueGiftBox]: processSuperValueGiftBoxCardData,
  [ActivityTypeEnum.vipLossRebate]: processVipLossRebateCardData,
  [ActivityTypeEnum.vipDayDeposit]: processVipDailyDepositBonusCardData,
  [ActivityTypeEnum.shakeRedPacket]: processShakeRedPacketBonusCardData,
  [ActivityTypeEnum.vipMonthBonus]: processVipMonthBonusCardData,
  [ActivityTypeEnum.weeklyRebate]: processWeeklyRebateCardData,
  [ActivityTypeEnum.propsItem]: processPropsItemCardData,
  [ActivityTypeEnum.appdownload]: processAppDownloadCardData,
  [ActivityTypeEnum.vipWeeklyCard]: processVipWeeklyCardCardData,
  [ActivityTypeEnum.sevenNewTask]: process7DayNewbieTaskCardData,
  [ActivityTypeEnum.firstDeposit]: processFirstDepositCardData,
  [ActivityTypeEnum.roulette]: processRouletteCardData,
  [ActivityTypeEnum.flipCard]: processFlipCardCardData,
  [ActivityTypeEnum.dailyLossBack]: processDailyLossBackCardData,
  [ActivityTypeEnum.dailyCashbackExperience]:
    processDailyCashbackExperienceCardData,
  [ActivityTypeEnum.cashFlop]: processCashFlopCardData,
} as const;

// 数据获取函数
const getBonusCenterCardData = (atype: number): BonusCardData => {
  const item = getActivityCfg(atype);
  const details = getActivityDetails(atype);
  if (!item || !details) {
    console.log(`Activity config or details not found for type ${atype}`);
    return { hide: true } as BonusCardData;
  }
  // 特殊处理Afun的92号卡片
  const img_name = atype === 92 ? (isAfun() ? `afun/92` : "92_2") : atype;

  return {
    atype,
    order: item.sort,
    other: {},
    active_img: getContentByLang(item.active_img),
    details: details.details,
    rewards: details.rewards,
    details_status: details.details_status,
    hide:
      !item.status ||
      details.activity_status === 0 ||
      details.details_status?.new_status === ActivityDetailStatus.close ||
      details.details_status?.new_status === ActivityDetailStatus.Finish,
    bg: isMobile()
      ? "/icons/bonus/card/card_h5_green.svg"
      : "/icons/bonus/card/card_pc_green.svg",
    request: item.status,
    title: "",
    vip: false,
    img: getImgurl(`${img_name}.png?10086`, "bonusCenter/cardIcon2"),
    content: "",
    status: details.details_status?.new_status,
    button: "",
    bg_mode: BonusBgMode.blue,
    text: "" as string | Record<string, string>[],
    hasRules: VipActList.includes(atype),
    readyList: [],
  };
};

export const getCardDataList = (index: number): BonusCardData[] => {
  const listMap = {
    0: getNewComerActData,
    1: getNormalActData,
    2: getVipActData,
    3: getSpecialActData,
  };
  return listMap[index as keyof typeof listMap]?.() || [];
};

export const getCategorySelectOption = (): Option[] => {
  const list = bonusCenterStore.bonusSumaryData?.specialHistoryTypeList?.map(
    (el) => ({
      label: t(`bonus.category_${el}`),
      value: String(el),
    }),
  );
  return [{ label: t("bonus.all_bonuses"), value: "0" }, ...list];
};

export const getCategoryName = () => {
  const category = bonusCenterStore.bonusTransaction?.data?.bonus_category || 0;
  if (category == 0) {
    return t("bonus.all_bonuses");
  }
  return t(`bonus.category_${category}`);
};

export const getNewComerActData = (): BonusCardData[] =>
  newComerActList
    .map((element) => bonusCenterStore.bonusCenterCardData[element])
    ?.filter(Boolean);

export const getNormalActData = (): BonusCardData[] =>
  NormalActList.map(
    (element) => bonusCenterStore.bonusCenterCardData[element],
  )?.filter(Boolean);

export const getVipActData = (): BonusCardData[] =>
  VipActList.map(
    (element) => bonusCenterStore.bonusCenterCardData[element],
  )?.filter(Boolean);

export const getSpecialActData = (): BonusCardData[] => [];

export const changeTabIndex = (index: number) => {
  setBonusCenterStore("pageData", (prev) => ({
    ...prev,
    tabIndex: index,
  }));
};

export const setBonusSumaryData = (bonusSumaryData: SumData) => {
  setBonusCenterStore("bonusSumaryData", reconcile(bonusSumaryData));
};

export const initBonusCenterRepeatCompetition = () => {
  getUserActivityDetails().then((res) => {
    const repeatCompetitionDetails = repeatCompetitionStore.details || {};
    const repeatCompetitionConfig = repeatCompetitionStore.config || {};
    const repeatCompetitionRank = repeatCompetitionStore.rank || [];
    const keys = Object.keys(repeatCompetitionDetails);
    const status = keys.some((el) => repeatCompetitionDetails[el].rank > -1)
      ? ActivityDetailStatus.canReceive
      : ActivityDetailStatus.check;
    const img = getImgurl(
      `${RepeatActivity.RepeatCompetition}.png?238`,
      "bonusCenter/cardIcon2",
    );
    const cardData = {
      atype: RepeatActivity.RepeatCompetition,
      order: RepeatActivity.RepeatCompetition,
      other: {},
      active_img: "",
      details: {},
      rewards: {},
      details_status: {
        status: status,
        new_status: status,
        countdown: 0,
        un_lock_lv: 0,
        current_point: 0,
      },
      hide: !keys.length,
      bg: isMobile()
        ? getImgurl("h5_bj_2.jpg", "bonusCenter/cardBg")
        : getImgurl("pc_bj_2.jpg", "bonusCenter/cardBg"),
      request: false,
      title: t("tournament.title"),
      vip: false,
      img: img,
      content: "",
      status: keys.some((el) => repeatCompetitionDetails[el].rank > -1)
        ? ActivityDetailStatus.canReceive
        : ActivityDetailStatus.check,
      button: t("common.details"),
      bg_mode: BonusBgMode.yellow,
      text: t("bonus.win_ranking_rewards"),
      hasRules: false,
      readyList: keys.reduce((res, cur) => {
        const data = repeatCompetitionDetails[cur];
        const config = repeatCompetitionConfig[cur];
        if (repeatCompetitionDetails[cur].rank > -1) {
          const activeImg = getContentByLang(config.active_img) || img;
          const find = repeatCompetitionRank.find(
            (el) => el.activity_id == cur,
          );
          res.push({
            type: 3,
            atype: RepeatActivity.RepeatCompetition,
            img: activeImg,
            order: RepeatActivity.RepeatCompetition,
            title: data.activity_name,
            button: "",
            countdown:
              (find?.rank_type == RepeatCompetitionRankType.within
                ? find?.last_time
                : find?.out_site_end_time) ||
              config.etime ||
              0,
            status: ActivityDetailStatus.toReceive,
            receive_point: 0,
            tips: `<div>
              <div class="bg-[linear-gradient(90deg,var(--general-yellow-l),var(--general-yellow-r))] bg-clip-text text-transparent text-[12px] font-semibold">${t("tournaments.unlocks_reward", { val: renderFiatOrPlatCoinSymbol(data.reward_point) })}</div>
              <div class="text-[12px] text-general-light font-medium">${t("bonus.tournament", { val: data.rank })}</div>
            </div>`,
            id: 0,
          });
        }
        return res;
      }, [] as any[]),
    };
    // processCompetitionCardData(cardData, {});
    setBonusCenterStore(
      "bonusCenterCardData",
      RepeatActivity.RepeatCompetition,
      cardData,
    );
    updateTabList();
  });
};
export const initAllDetail = () => {
  initAllActivityDetail();
  updateTabList();
  initData().then(() => {
    initBonusCenterRepeatCompetition();
  });
};

export const initAllActivityDetail = () => {
  const { actConfigData, actDetailsData } = activityStore;
  const keys = Object.keys(actDetailsData)
    .map(Number)
    ?.filter(
      (el) =>
        NormalActList.includes(el) ||
        VipActList.includes(el) ||
        newComerActList.includes(el) ||
        wagerContestActList.includes(el),
    );

  keys.forEach((atype) => {
    let cardData = getBonusCenterCardData(atype);
    const processor =
      activityProcessors[atype as keyof typeof activityProcessors];

    if (processor) {
      cardData = processor(cardData, actConfigData[atype]) as BonusCardData;
    }

    setBonusCenterStore("bonusCenterCardData", atype, cardData);
  });
  const prop = processPropsItemCardData(
    // 道具列表特殊处理
    getBonusCenterCardData(ActivityTypeEnum.propsItem),
  );
  setBonusCenterStore("bonusCenterCardData", ActivityTypeEnum.propsItem, prop);
  updateWagerContestCardData();
  updateTabList();
};

const updateWagerContestCardData = () => {
  const wagerconst = processWagerContestCardData(
    // 投注比赛
    getBonusCenterCardData(ActivityTypeEnum.wagerContest),
  );
  setBonusCenterStore(
    "bonusCenterCardData",
    ActivityTypeEnum.wagerContest,
    wagerconst,
  );
};

const updateTabList = () => {
  for (let index = 0; index < bonusCenterStore.tabList.length; index++) {
    const realIndex = bonusCenterStore.tabList[index].index;
    if (realIndex == 3) {
      const count = bonusCenterStore.specialList?.filter(
        (el: any) => el.is_receivable,
      ).length;
      setBonusCenterStore("tabList", realIndex, "count", count);
      setBonusCenterStore("tabList", realIndex, "type", 1);
      continue;
    }
    setBonusCenterStore(
      "tabList",
      realIndex,
      "count",
      getCardDataList(realIndex)?.filter(
        (el) =>
          el.status === ActivityDetailStatus.canReceive ||
          el.status === ActivityDetailStatus.canComplete ||
          el.status === ActivityDetailStatus.toReceive,
      ).length,
    );

    const haveCanReceive = getCardDataList(realIndex)?.some(
      (el) => el.status === ActivityDetailStatus.canReceive,
    );
    const haveToReceive = getCardDataList(realIndex)?.some(
      (el) => el.status === ActivityDetailStatus.toReceive,
    );
    const haveCanComplete = getCardDataList(realIndex)?.some(
      (el) => el.status === ActivityDetailStatus.canComplete,
    );
    let type = 0;
    if (haveCanReceive) {
      type = 1; //绿点
    } else if (haveToReceive && !haveCanComplete) {
      type = 2; //黄点
    } else if (haveCanComplete) {
      type = 1; //绿点
    }

    setBonusCenterStore("tabList", realIndex, "type", type);
  }
};

export const getOneActivityDetail = async (atype: number) => {
  const { actDetailsData } = activityStore;
  return actDetailsData[atype];
};

export const updateDetailsData = (atype: number, data: NewActivityDetails) => {
  setActivityStore("actDetailsData", atype, reconcile(data));
  updateOneActivityDetail(atype);
};

export const updateOneActivityDetail = (atype: number) => {
  const { actConfigData } = activityStore;
  let cardData = getBonusCenterCardData(atype);
  if (wagerContestActList.includes(atype)) {
    updateWagerContestCardData();
  } else if (atype == ActivityTypeEnum.propsItem) {
    const prop = processPropsItemCardData(
      // 道具列表特殊处理
      getBonusCenterCardData(ActivityTypeEnum.propsItem),
    );
    setBonusCenterStore(
      "bonusCenterCardData",
      ActivityTypeEnum.propsItem,
      prop,
    );
  } else {
    const processor =
      activityProcessors[atype as keyof typeof activityProcessors];
    if (processor) {
      cardData = processor(cardData, actConfigData[atype]) as BonusCardData;
    }
    setBonusCenterStore("bonusCenterCardData", atype, cardData);
  }
  updateTabList();
};

export const refresh = () => {
  setBonusCenterStore("bonusTransaction", {
    finish: false,
    list: [],
    data: {
      page: INITIAL_PAGE,
      page_size: DEFAULT_PAGE_SIZE,
      bonus_category: bonusCenterStore.bonusTransaction.data.bonus_category,
    },
  });
  loadData();
};

export const updateBonusCategory = (value: string) => {
  if (value !== String(bonusCenterStore.bonusTransaction.data.bonus_category)) {
    setBonusCenterStore(
      "bonusTransaction",
      "data",
      "bonus_category",
      Number(value),
    );
    refresh();
  }
};

export const loadData = async () => {
  try {
    const { page, page_size, bonus_category } =
      bonusCenterStore.bonusTransaction.data;
    const body = {
      ...bonusCenterStore.bonusTransaction.data,
      page: String(page),
      page_size: String(page_size),
      bonus_category: Number(bonus_category),
    };

    const res = await postGetMyBonusTransaction(body as MyBonusTransactionReq);
    const list = (res?.recs || []).map((el: MyBonusTransactionRec) => ({
      ...el,
      web_render_time: formatDate(el.recv_at, 1),
    }));

    const isComplete = list.length < page_size;
    setBonusCenterStore(
      "bonusTransaction",
      "data",
      "page",
      isComplete ? page : page + 1,
    );
    setBonusCenterStore("bonusTransaction", "finish", isComplete);
    setBonusCenterStore("bonusTransaction", "list", (prev: any) => [
      ...prev,
      ...list,
    ]);
  } catch (error) {
    setBonusCenterStore("bonusTransaction", "finish", true);
    console.error("Failed to load bonus data:", error);
  }
};

export const clearBonusCenterStore = () => {
  setBonusCenterStore({
    bonusCenterCardData: {},
    bonusSumaryData: {
      totalActivity: 0,
      totalVip: 0,
      totalAll: 0,
      totalSpecial: 0,
      specialHistoryTypeList: [],
      specialHistoryMaps: {},
    },
    pageData: {
      tabIndex: 0,
      ruleAtype: 0,
      category: "",
    },
    tabList: [
      { title: "Newcomers", index: 0, count: 0, type: 1 },
      { title: "General", index: 1, count: 0, type: 1 },
      { title: "VIP", index: 2, count: 0, type: 1 },
      { title: "Special", index: 3, count: 0, type: 1 },
    ],
    bonusTransaction: {
      finish: false,
      list: [],
      data: {
        page: INITIAL_PAGE,
        page_size: DEFAULT_PAGE_SIZE,
        bonus_category: DEFAULT_BONUS_CATEGORY,
      },
    },
  });
};

export const setRedeemRuleSwitch = (value: boolean) => {
  setBonusCenterStore("redeemRuleSwitch", value);
};

export const setRedeemWagerSwitch = (value: boolean) => {
  setBonusCenterStore("redeemWagerSwitch", value);
};

const getReadyItem = (data: BonusCardData, other: any) => {
  if (
    data &&
    !data.hide &&
    (data.status == ActivityDetailStatus.canReceive ||
      data.status == ActivityDetailStatus.canComplete ||
      data.status == ActivityDetailStatus.toReceive)
  ) {
    const type = data.status == ActivityDetailStatus.canReceive ? 2 : 1;
    const item = {
      type,
      atype: data.atype,
      img: data.active_img || data.img,
      order: data.order,
      title: data.title,
      button: "",
      status: ActivityDetailStatus.check,
      receive_point: 0,
      id: 0,
    };
    Object.assign(item, other);
    if (item.type == 2) {
      item.button = t("common.claim");
      if (!item.receive_point) {
        item.receive_point = data.rewards?.receive_point || 0;
      }
    }
    if (item.atype == ActivityTypeEnum.mysteriousRewards) {
      item.type = 2;
      item.receive_point = data.details.point;
    }

    if (item.atype == ActivityTypeEnum.reliefFund3) {
      item.receive_point = data.details?.receivable_point || 0;
    }

    if (item.atype == ActivityTypeEnum.flipCard) {
      const isReceivable = data.details?.key_rewards?.some((el: any) => {
        return el.receivable_at * 1000 < Date.now();
      });

      if (isReceivable) {
        item.button = t("common.claim");
      } else if (data.status == ActivityDetailStatus.canComplete) {
        item.button = t("flip_card.flip");
      }
    }

    return item;
  }
  return null;
};

export const {
  redeemRuleSwitch,
  redeemWagerSwitch,
  readyList,
  generalReadyList,
  vipReadyList,
  newcomerReadyList,
  readyListCount,
  tabList,
  redemptionTitle,
  redemptionRules,
  readyListType,
} = createRoot(() => {
  const redeemRuleSwitch = () => {
    return bonusCenterStore.redeemRuleSwitch;
  };
  const redeemWagerSwitch = () => {
    return bonusCenterStore.redeemWagerSwitch;
  };
  const readyList = createMemo(() => {
    // 直接访问 store 以确保响应式追踪
    const cardData = bonusCenterStore.bonusCenterCardData;

    // 收集所有活动的 readyList
    const allReadyItems: ReadyItem[] = [];

    // 处理普通活动
    const generalActDataList =
      NormalActList.map((element) => cardData[element])?.filter(Boolean) || [];

    generalActDataList.forEach((el) => {
      if (el.readyList && Array.isArray(el.readyList)) {
        allReadyItems.push(...el.readyList);
      }
    });

    // 处理VIP活动
    const vipActDataList =
      VipActList.map((element) => cardData[element])?.filter(Boolean) || [];

    vipActDataList.forEach((el) => {
      if (el.readyList && Array.isArray(el.readyList)) {
        allReadyItems.push(...el.readyList);
      }
    });

    // 处理新手活动
    const newcomerActDataList =
      newComerActList.map((element) => cardData[element])?.filter(Boolean) ||
      [];
    newcomerActDataList.forEach((el) => {
      if (el.readyList && Array.isArray(el.readyList)) {
        allReadyItems.push(...el.readyList);
      }
    });
    bonusCenterStore.repeatCompetitionRewards.forEach((el) => {
      const findActivtiyConfig =
        repeatCompetitionStore.config[el.activity_id] || {};
      const findActivityDetail =
        repeatCompetitionStore.details[el.activity_id] || {};
      const readyItemData: BonusCardData = {
        hide: false,
        status: ActivityDetailStatus.canReceive,
        img:
          getContentByLang(findActivtiyConfig.active_img || {}) ||
          getImgurl(
            `${RepeatActivity.RepeatCompetition}.png?238`,
            "bonusCenter/cardIcon2",
          ),
        title: findActivityDetail.activity_name,
        order: el.activity_type,
        atype: el.activity_type,
        request: false,
        button: "",
        content: "",
        other: {},
        active_img: getContentByLang(findActivtiyConfig.active_img),
        details: {},
        rewards: { ...el, receive_point: el.point },
        details_status: {
          status: 1,
          new_status: 1,
          countdown: 0,
          un_lock_lv: 0,
          current_point: 0,
        },
        bg: "",
        vip: false,
        bg_mode: BonusBgMode.yellow,
        hasRules: false,
        readyList: [],
        text: "",
      };
      allReadyItems.push(
        getReadyItem(readyItemData, {
          id: el.id,
          is_special: true,
          reward: el,
        }),
      );
    });
    // 按 order 排序以确保稳定的索引，并添加 atype 作为次要排序条件
    return allReadyItems.sort((a, b) => {
      // 状态优先级：canReceive > toReceive > canComplete
      const statusA = cardData[a.atype]?.status;
      const statusB = cardData[b.atype]?.status;

      if (statusA !== statusB) {
        // 定义状态优先级映射
        const statusPriority: Partial<Record<ActivityDetailStatus, number>> = {
          [ActivityDetailStatus.canReceive]: 1, // 最高优先级
          [ActivityDetailStatus.toReceive]: 2, // 中等优先级
          [ActivityDetailStatus.canComplete]: 3, // 较低优先级
        };

        const priorityA = statusPriority[statusA] || 999;
        const priorityB = statusPriority[statusB] || 999;

        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }
      }
      if (a.order !== b.order) {
        return a.order - b.order;
      }
      return a.atype - b.atype;
    });
  });
  const generalReadyList = createMemo(() => {
    // 直接访问 store 以确保响应式追踪
    const cardData = bonusCenterStore.bonusCenterCardData;
    const generalActDataList =
      NormalActList.map((element) => cardData[element])?.filter(Boolean) || [];
    const list: ReadyItem[] = [];

    generalActDataList.forEach((el) => {
      if (el.readyList && Array.isArray(el.readyList)) {
        list.push(...el.readyList);
      }
    });

    // 按 order 排序以确保稳定的索引，并添加 atype 作为次要排序条件
    list.sort((a, b) => {
      if (a.order !== b.order) {
        return a.order - b.order;
      }
      return a.atype - b.atype;
    });
    return list;
  });
  const vipReadyList = createMemo(() => {
    // 直接访问 store 以确保响应式追踪
    const cardData = bonusCenterStore.bonusCenterCardData;
    const vipActDataList =
      VipActList.map((element) => cardData[element])?.filter(Boolean) || [];
    const list: ReadyItem[] = [];

    vipActDataList.forEach((el) => {
      if (el.readyList && Array.isArray(el.readyList)) {
        list.push(...el.readyList);
      }
    });

    // 按 order 排序以确保稳定的索引，并添加 atype 作为次要排序条件
    return list.sort((a, b) => {
      if (a.order !== b.order) {
        return a.order - b.order;
      }
      return a.atype - b.atype;
    });
  });
  const redemptionTitle = createMemo(() => t("bonus.redeem_rules_title"));
  const redemptionRules = createMemo(() => {
    const rules = [t("bonus.redeem_rules_1"), t("bonus.redeem_rules_2")];

    if (sysConfigStore.sysConfigData.exchange_over_bonus_code) {
      rules.push(
        t("bonus.redeem_rules_3", {
          val: sysConfigStore.sysConfigData.exchange_over_bonus_code,
        }),
      );
    }
    if (sysConfigStore.sysConfigData.exchange_bonus_max_for_regen_code) {
      rules.push(
        t("bonus.redeem_rules_4", {
          val1: formatToBindPoint({
            value:
              sysConfigStore.sysConfigData.exchange_bonus_max_for_regen_code,
            isReward: true,
            isConverted: false,
          }),
          val2: formatToBindPoint({
            value:
              sysConfigStore.sysConfigData.exchange_bonus_max_for_regen_code,
            isReward: true,
            isConverted: false,
          }),
        }),
      );
    }
    if (sysConfigStore.sysConfigData.bet_bonus_max_once) {
      rules.push(
        t("bonus.redeem_rules_5", {
          val: formatToBindPoint({
            value: sysConfigStore.sysConfigData.bet_bonus_max_once,
            isReward: true,
            isConverted: false,
          }),
        }),
      );
    }
    rules.push(t("bonus.redeem_rules_6"));
    return rules;
  });

  const newcomerReadyList = createMemo(() => {
    // 直接访问 store 以确保响应式追踪
    const cardData = bonusCenterStore.bonusCenterCardData;
    const newcomerActDataList =
      newComerActList.map((element) => cardData[element])?.filter(Boolean) ||
      [];
    const list: ReadyItem[] = [];

    newcomerActDataList.forEach((el) => {
      if (el.readyList && Array.isArray(el.readyList)) {
        list.push(...el.readyList);
      }
    });

    // 按 order 排序以确保稳定的索引，并添加状态优先级和 atype 作为次要排序条件
    return list.sort((a, b) => {
      if (a.order !== b.order) {
        return a.order - b.order;
      }

      return a.atype - b.atype;
    });
  });
  const readyListCount = createMemo(() => {
    return bonusCenterStore.tabList.reduce((acc, curr) => {
      if (curr.index !== 3) {
        return acc + curr.count;
      } else {
        return acc;
      }
    }, 0);
  });
  const readyListType = createMemo(() => {
    const haveCanReceive = bonusCenterStore.tabList.some((el) => el.type === 1);
    const haveToReceive = bonusCenterStore.tabList.some((el) => el.type === 2);

    const type = 0;
    if (haveCanReceive) {
      return 1;
    } else if (haveToReceive) {
      return 2;
    }
    return 1;
  });
  const tabList = createMemo(() => {
    return bonusCenterStore.tabList?.filter((item) => {
      if (item.index == 0) {
        return (
          Object.values(bonusCenterStore.bonusCenterCardData)?.filter(
            (item) =>
              newComerActList.includes(item.atype) &&
              item.status !== ActivityDetailStatus.Finish &&
              item.status !== ActivityDetailStatus.close,
          ).length > 0
        );
      }
      if (item.index == 1) {
        return (
          Object.values(bonusCenterStore.bonusCenterCardData)?.filter(
            (item) =>
              NormalActList.includes(item.atype) &&
              item.status !== ActivityDetailStatus.Finish &&
              item.status !== ActivityDetailStatus.close,
          ).length > 0
        );
      }
      if (item.index == 2) {
        return (
          Object.values(bonusCenterStore.bonusCenterCardData)?.filter(
            (item) =>
              VipActList.includes(item.atype) &&
              item.status !== ActivityDetailStatus.Finish &&
              item.status !== ActivityDetailStatus.close,
          ).length > 0
        );
      }
      if (item.index == 3) {
        return bonusCenterStore.specialList.length > 0;
      }
      return false;
    });
  });

  return {
    redeemRuleSwitch,
    redeemWagerSwitch,
    readyList,
    generalReadyList,
    vipReadyList,
    newcomerReadyList,
    readyListCount,
    tabList,
    redemptionTitle,
    redemptionRules,
    readyListType,
  };
});
